import React, { Fragment } from 'react';
import DatePicker from 'react-datepicker';
import {
    Badge,
    Button,
    ButtonGroup,
    Nav,
    NavItem,
    NavLink,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Progress,
    Container,
    Row,
    Col,
    UncontrolledTooltip,
} from 'reactstrap';
import {
    debounce, groupBy, uniq,
    intersection, uniqBy,
    isEmpty,
} from 'lodash-es';
import cls from 'classnames';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import moment from 'moment';
import {
    faCircleNotch,
    faStar,
    faBolt,
    faCog,
    faFilter,
    faMagnifyingGlass,
    faSearch,
    faChevronLeft,
    faChevronRight,
    faUser,
    faTruck,
    faChevronUp,
    faChevronDown,
    faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@s20/components/common/Link';
import { weekdayLetter } from '@s20/i18n';
import { Hide } from '@s20/components/common/Hide';
import {
    appendTimeZone,
    formatTime12Hour,
    getDaysInRange,
    getInputFormat,
} from '@s20/common/util/date';
import { pluralize } from '@s20/common/util/l10n';
import { getCounty } from '@s20/common/util/location';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, {
    ApiRoutes,
    AppNavPaths,
    ApplicationPermissions,
    CustomFormContext,
    EventStatus,
    Weekdays,
} from '../Common';
import CustomForms from '../customForm/CustomForms';
import {
    onFieldChange,
    toasty,
    FormCheckbox,
    FormLabel,
    IconButton,
    Input,
    FlexCenterRow,
} from '../common/forms/FormElements';
import ValidatedSelect, { CompactSelectStyles } from '../common/forms/ValidatedSelect';
import { SplitHelper } from '../common/SplitHelper';
import CustomCircularProgress from '../common/CustomCircularProgress';
import authService from '../api-authorization/AuthorizeService';
import { CountyLabel } from '../uscounties/CountyLabel';
import { ControlledAccordionCollapse } from '../common/AccordionCollapse';
import { Block } from '../common/Block';
import Chip from '../common/forms/Chip';
import { Modal as Prompt } from '../common/Modal';
import { Split } from '../common/Split';
import EquipmentCard from './EquipmentCard';
import DispatchJobAssignments from './DispatchJobAssignments';
import DispatchCancellationJobAssignments from './DispatchCancellationJobAssignments';
import JobWorkflowEditor from './JobWorkflowEditor';
import DailyAttendanceDispatchSink from './DailyAttendanceDispatchSink';
import AttendanceAssignmentFooter from './AttendanceAssignmentFooter';
import DispatchEmployeeFilter from './DispatchEmployeeFilter';
import DispatchEquipmentFilter from './DispatchEquipmentFilter';
import MobileAssignmentsModal from './MobileAssignmentsModal';
import { EquipmentAssignmentModal } from './EquipmentAssignmentModal';
import DispatchEmployeeEvents from './DispatchEmployeeEvents';
import { QuickJobCreatedAlert } from './QuickJobCreatedAlert';
import StaffCard from './StaffCard';
import { evaluateJobTitle } from './evaluateJobTitle';
import {
    DispatchJobTabs,
    DispatchJobTabNumbers,
    DispatchResourceTabNumbers,
    DispatchResourceTabs,
    DispatchBoardViewModel,
    DispatchResourceSearchParams,
    DispatchJobSearchParams,
    getJobAssignmentCancellationTypeIdsThatPreventReassignment,
    isCancelAssignmentTreatedAsCancelled,
    DraggableResourceTypes,
    CrossDispatchJobSearchParams,
    FilterTypes,
    JobStatusTypes,
    getEligibleStaffForEquipment,
    getEligibleEquipmentForEquipment,
    makeAssignmentItem,
    getLinkedEquipmentCandidates,
    doesJobPassSearchText,
    getPlanningBoard,
    doesJobPassPlanningFilter,
    isDayWithinLongTermStatus,
} from './Dispatch';
import { CancelJob } from './CancelJob';
import './DispatchBoard.scss';
import { CancelJobAssignment } from './CancelJobAssignment';
import { PlanningBoard } from './PlanningBoard';
import { DispatchHub } from './DispatchHub';

const groupJobsByParent = (jobs) => {
    const grouped = groupBy(jobs, (job) => job.parentJobId || job.id);

    // pull out the keys in exactly the order they first appear
    const keysInOrder = uniq(jobs.map((job) => job.parentJobId || job.id));

    // map those keys back to the grouped arrays
    return keysInOrder.map((key) => grouped[key]);
};

const getDailyAttendanceEventRestrictionMessage = (
    employee,
    assignment,
    dailyAttendanceEvents,
    employeeDailyAttendanceEvents,
) => {
    const dateInQuestion = moment(assignment.assignmentDate);

    const employeeDailyAttendanceEvent = employeeDailyAttendanceEvents.find(
        (edae) => edae.employeeId == employee.id
            && moment(dateInQuestion).isSame(edae.eventDate, 'day'),
    );

    if (!employeeDailyAttendanceEvent) {
        return null;
    }

    const dailyAttendanceEvent = dailyAttendanceEvents.find(
        (dae) => dae.id == employeeDailyAttendanceEvent.dailyAttendanceEventId,
    );

    if (
        dailyAttendanceEvent
        && dailyAttendanceEvent.employeeMayNotBeAssigned
    ) {
        return `${employee.name} has a Daily Attendance Event of "${dailyAttendanceEvent.name}" applied on ${moment(assignment.assignmentDate).format('ddd M/D')}.`;
    }

    return null;
};

const getCertificationValidationMessage = (sourceData, assignmentDate) => {
    const jobDate = moment(assignmentDate);
    const response = sourceData.employeeCertifications
        ?.filter((cert) => cert.certification.mustBeValidForDispatch)
        .find((cert) => {
            const effective = cert.effectiveDate ? moment(cert.effectiveDate) : null;
            const { willExpire } = cert.certification;
            const expires = cert.expirationDate ? moment(cert.expirationDate) : null;

            if (effective && effective.isAfter(jobDate, 'day'))
                return true;

            if (willExpire && expires && expires.isBefore(jobDate, 'day')) return true;

            return false;
        });
    if (!response) return null;
    const { certification } = response;
    const { name } = certification;
    const eff = response.effectiveDate
        ? moment(response.effectiveDate).format('MMM D YYYY')
        : '';


    const exp = response.certification.willExpire && response.expirationDate
        ? moment(response.expirationDate).format('MMM D YYYY')
        : '';

    const exppart = exp ? `, expires ${exp}` : '';

    return `${name} is not valid on ${jobDate.format(
        'MMM D YYYY',
    )} (effective ${eff}${exppart}).`;
};


const getEmployeeRestrictionMessage = (employee,
    job, assignmentDate) => {
    const overrideForDate = job.overrides.find(
        (override) => moment(override.overrideDate).isSame(assignmentDate, 'day'),
    );

    const jobForemanId = overrideForDate?.foremanId ?? job.foremanId;
    const employeeRestriction = employee?.employeeRestrictions.find(
        (res) => res.companyId === job.companyId &&
            (
                res.blockAll === true ||
                (res.blockAll === false && res.locationContactId === jobForemanId)

            ),
    );

    if (employeeRestriction) {
        return (
            `${employee.name} cannot be assigned to this job on ` +
            `${new Date(assignmentDate).toDateString()}.\n` +
            'A restriction is in place that prevents assignments to ' +
            `${job.contractCompanyName}.\n` +
            'Please choose a different employee or select a different job.'
        );
    }
    return null;
};

const getEmployeeToEmployeeRestrictionMessage = (
    employee, allAssignmentsForTheDayAndJob, allStaff, assignmentDate,
) => {
    // Get IDs of employees already assigned to this job on this day
    const assignedEmployeeIds = allAssignmentsForTheDayAndJob
        .filter((s) => !!s.assignment)
        .map((s) => s.assignment.id);

    if (!assignedEmployeeIds.length) return null;

    // Forward check: does the dragged employee have a restriction against an assigned employee?
    const forwardRestriction = employee?.employeeRestrictions?.find(
        (res) => res.restrictedEmployeeId && assignedEmployeeIds.includes(res.restrictedEmployeeId),
    );

    if (forwardRestriction) {
        const assignedSlot = allAssignmentsForTheDayAndJob.find(
            (s) => s.assignment?.id === forwardRestriction.restrictedEmployeeId,
        );
        const restrictedName = assignedSlot?.assignment?.name ?? 'another employee';
        return (
            `${employee.name} cannot be assigned to this job on ` +
            `${new Date(assignmentDate).toDateString()}.\n` +
            `An employee restriction is in place between ${employee.name} and ${restrictedName}.\n` +
            'Please choose a different employee or select a different job.'
        );
    }

    // Reverse check: does any assigned employee have a restriction against the dragged employee?
    for (const assignedId of assignedEmployeeIds) {
        const assignedEmployee = allStaff?.find((s) => s.id === assignedId);
        const reverseRestriction = assignedEmployee?.employeeRestrictions?.find(
            (res) => res.restrictedEmployeeId === employee.id,
        );
        if (reverseRestriction) {
            return (
                `${employee.name} cannot be assigned to this job on ` +
                `${new Date(assignmentDate).toDateString()}.\n` +
                `An employee restriction is in place between ${employee.name} and ${assignedEmployee.name}.\n` +
                'Please choose a different employee or select a different job.'
            );
        }
    }

    return null;
};


const getTimeBasedRestrictionMessage = (employee, job, assignmentDate) => {
    const timeRestrictions = employee?.employeeRestrictions?.filter(
        (r) => r.employeeRestrictionTypeId === 3,
    );
    if (!timeRestrictions?.length) return null;

    const dayOfWeek = new Date(assignmentDate).getDay();
    const dayBit = 1 << dayOfWeek;

    let jobMinutes = null;
    if (job.startTime) {
        const iso = moment.parseZone(job.startTime, moment.ISO_8601, true);
        const t = iso.isValid() ? iso : moment(job.startTime, 'h:mm A');
        if (t.isValid()) {
            jobMinutes = t.hours() * 60 + t.minutes();
        }
    }

    let blockedByTimeRange = null;
    const isBlocked = timeRestrictions.some((r) => {
        if (r.restrictionDays && (r.restrictionDays & dayBit)) return true;
        if (r.restrictionStartTime && r.restrictionEndTime && jobMinutes !== null) {
            const [sh, sm] = r.restrictionStartTime.split(':').map(Number);
            const [eh, em] = r.restrictionEndTime.split(':').map(Number);
            const start = sh * 60 + sm;
            const end = eh * 60 + em;
            const isOvernight = start > end;
            const hit = isOvernight
                ? jobMinutes >= start || jobMinutes < end
                : jobMinutes >= start && jobMinutes < end;
            if (hit) blockedByTimeRange = r;
            return hit;
        }
        return false;
    });

    if (!isBlocked) return null;

    const detail = blockedByTimeRange
        ? `from ${formatTime12Hour(blockedByTimeRange.restrictionStartTime)} – ${formatTime12Hour(blockedByTimeRange.restrictionEndTime)}`
        : `on ${moment(assignmentDate).format('ddd')}`;

    return (
        `${employee.name} has a time-based availability restriction ${detail}.\n` +
        'Please choose a different employee or a different job.'
    );
};

const getStaffLongTermStatusRestrictionMessage = (
    employee,
    assignment,
    longTermStatuses,
    employeeLongTermStatuses,
    warningsArray,
) => {
    const dateInQuestion = moment(assignment.assignmentDate).startOf('day');

    const effectiveEmployeeLongTermStatus = (employeeLongTermStatuses || []).find(
        (elts) => {
            const startDayUtc = moment(elts.startDate).startOf('day');
            const hasEnd = !!elts.endDate;
            const endDayUtc = hasEnd ? moment(elts.endDate).endOf('day') : null;

            let isInRange = dateInQuestion.isSameOrAfter(startDayUtc, 'day');

            if (hasEnd) {
                isInRange = dateInQuestion.isBetween(startDayUtc, endDayUtc, 'day', '[]');
            }

            return isInRange && elts.employeeId == employee.id;
        },
    );

    if (!effectiveEmployeeLongTermStatus) {
        return null;
    }

    const effectiveLongTermStatus = longTermStatuses.find(
        (lts) => lts.id == effectiveEmployeeLongTermStatus.longTermStatusId,
    );

    if (
        !effectiveLongTermStatus
        || (effectiveLongTermStatus.allowDispatch
            && !effectiveLongTermStatus.hideFromDispatchBoard)
    ) {
        if (effectiveLongTermStatus && effectiveLongTermStatus.allowDispatch) {
            const warning = `Employee With Long-Term Status Assigned : ${employee.name} has a Long-Term Status of "${effectiveLongTermStatus.name}", but this did not prevent assignment.`;
            warningsArray.push(warning);
        }

        return null;
    }

    return `${employee.name} has a Long-Term Status of "${effectiveLongTermStatus.name}" applied on ${moment(assignment.assignmentDate).format('ddd M/D')}.`;
};

const getEquipmentLongTermStatusRestrictionMessage = (
    equipment,
    assignment,
    longTermStatuses,
    equipmentLongTermStatuses,
    warningsArray,
) => {
    const dateInQuestion = moment(assignment.assignmentDate).startOf('day');

    const effectiveEquipmentLongTermStatus = (equipmentLongTermStatuses || []).find(
        (elts) => {
            const startDayUtc = moment(elts.startDate).startOf('day');
            const hasEnd = !!elts.endDate;
            const endDayUtc = hasEnd ? moment(elts.endDate).endOf('day') : null;

            let isInRange = dateInQuestion.isSameOrAfter(startDayUtc, 'day');

            if (hasEnd) {
                isInRange = dateInQuestion.isBetween(startDayUtc, endDayUtc, 'day', '[]');
            }

            return isInRange && elts.equipmentId == equipment.id;
        },
    );

    if (!effectiveEquipmentLongTermStatus) {
        // We only allow pickups if they have a long-term status unless the equipment type is generic
        if (assignment.isRentalPickup && !equipment.equipmentType.isGeneric) {
            return `${equipment.description} does not currently have a Long-Term Status applied and is not considered to be on-site.`;
        }

        return null;
    }

    const effectiveLongTermStatus = longTermStatuses.find(
        (lts) => lts.id == effectiveEquipmentLongTermStatus.longTermStatusId,
    );

    if (
        !effectiveLongTermStatus
        || (effectiveLongTermStatus.allowDispatch
            && !effectiveLongTermStatus.hideFromDispatchBoard
            && !assignment.isRentalPickup
        )
    ) {
        if (effectiveLongTermStatus && effectiveLongTermStatus.allowDispatch) {
            const warning = `Equipment With Long-Term Status Assigned: ${equipment.description} has a Long-Term Status of "${effectiveLongTermStatus.name}", but this did not prevent assignment.`;
            warningsArray.push(warning);
        }

        return null;
    }

    if (assignment.isRentalPickup) {
        return null;
    }

    return `${equipment.description} has a Long-Term Status of "${effectiveLongTermStatus.name}" applied on ${moment(assignment.assignmentDate).format('ddd M/D')}.`;
};

const getPreferredDispatch = () => {
    if (!window.localStorage) return null;

    const preferredDispatch = localStorage.getItem(
        'preferredDispatchLocation',
    );

    if (preferredDispatch) return parseInt(preferredDispatch);
    return null;
};

const showCollectedErrors = (errors = []) => {
    if (!errors.length) return;

    // Filter out duplicates
    const uniqueErrors = [...new Set(errors)];

    // Format messages with bullet points
    const message = uniqueErrors
        .map((err) => `• ${err}`)
        .join('\n\n');

    toasty.error(
        'Assignment Cannot Be Made',
        message,
        {
            autoClose: true,
            closeOnClick: true,
            style: { whiteSpace: 'pre-line' },
            toastId: `errors-${Date.now()}`,
        },
    );
};

const showCollectedWarnings = (warnings = []) => {
    if (!warnings.length) return;

    // Filter out duplicates
    const uniqueWarnings = [...new Set(warnings)];

    // Format messages with bullet points
    const message = uniqueWarnings
        .map((warn) => `• ${warn}`)
        .join('\n\n');

    toasty.warning(
        'Assignment Warnings',
        message,
        {
            autoClose: true,
            closeOnClick: true,
            style: { whiteSpace: 'pre-line' },
            toastId: `warnings-${Date.now()}`,
        },
    );
};

class DispatchBoard extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            ...new DispatchBoardViewModel(),
            selectedEmergencyAssignments: null,
            selectedEmergencyJobId: null,
            selectedEmergencyDay: null,
            showEmergencyConfirmModal: false,
            isCallingOutAssignment: false,
            dispatchEquipmentPermission: false,
            dispatchEmployeePermission: false,
            dispatchEditPermission: false,
            equipmentAuditLogPermission: false,
            showMobileDatePicker: false,
            // Mobile assignment modal state
            showMobileAssignmentModal: false,
            mobileAssignmentResourceType: null, // 'staff' or 'equipment'
            mobileAssignmentSlot: null, // The slot being assigned to
            mobileAssignmentSlotIndex: null, // The index of the slot being assigned
            mobileAssignmentJob: null, // The job being assigned to
            mobileAssignmentDay: null, // The day being assigned
            mobileSelectedResource: null, // The selected resource in the modal
            mobileIsBulkAssignment: false, // Toggle state for bulk assignment in mobile modal
            mobileSelectedStaffForEquipment: null, // Staff selected to assign to equipment in mobile modal
            usCounties: [],
            selectedAttendanceEvent: null,
            selectedStaffForAssignment: [],
            isAssigningAttendance: false,
            footerIsVisible: false,
            isCrewOnSiteAndWorkPerformed: false,
        };
        // instance binders
        this.onChange = this.onChange.bind(this);
        this.onAttendanceClick = this.onAttendanceClick.bind(this);
        this.onUnassignedClick = this.onUnassignedClick.bind(this);
        this.onCancelledClick = this.onCancelledClick.bind(this);
        this.onUnassignedEquipmentClick = this.onUnassignedEquipmentClick.bind(this);
        this.onJobCancellationUpdatedFromHub = this.onJobCancellationUpdatedFromHub.bind(this);
        this.onJobCreatedFromHub = this.onJobCreatedFromHub.bind(this);
        this.onJobUpdatedFromHub = this.onJobUpdatedFromHub.bind(this);
        this.onUpdateMultipleJobs = this.onUpdateMultipleJobs.bind(this);
        this.onJobsUpdatedBulkFromHub = this.onJobsUpdatedBulkFromHub.bind(this);
        this.onJobDeletedFromHub = this.onJobDeletedFromHub.bind(this);
        this.onStaffRelatedDataUpdatedFromHub = this.onStaffRelatedDataUpdatedFromHub.bind(this);
        this.onReconnectHub = this.onReconnectHub.bind(this);
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();

        // Add keydown and keyup listeners to track Ctrl key
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('keyup', this.handleKeyUp);
    }

    componentDidUpdate(prevProps) {
        const prevDate = new URLSearchParams(prevProps.location?.search ?? '').get('date');
        const nextDate = new URLSearchParams(this.props.location?.search ?? '').get('date');
        if (nextDate && nextDate !== prevDate) {
            const parsed = moment(nextDate, 'YYYY-MM-DD');
            if (parsed.isValid()) {
                this.handleMobileDateSelection(parsed.toDate());
            }
        }
    }

    componentWillUnmount() {
        authService.unsubscribe(this._subscription);

        // Remove key listeners
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('keyup', this.handleKeyUp);
    }

    handleKeyDown = (event) => {
        if (event.key === 'Control' && !this.state.isCtrlPressed) {
            this.setState({ isCtrlPressed: true });
        }
    };

    handleKeyUp = (event) => {
        if (event.key === 'Control' && this.state.isCtrlPressed) {
            this.setState({ isCtrlPressed: false });
        }
    };


    handleDispatchLocationChange() {
        if (!this.state.dispatchLocations) {
            return;
        }

        const staffIndex = DispatchResourceTabs.findIndex(
            (tab) => tab.name === 'Staff',
        );
        const equipmentIndex = DispatchResourceTabs.findIndex(
            (tab) => tab.name === 'Equipment',
        );

        const staffTab = DispatchResourceTabs[staffIndex];
        const equipmentTab = DispatchResourceTabs[equipmentIndex];

        const shouldSetStaffActive =
            this.state.selectedResourceLocation !== this.state.selectedJobDispatchLocation &&
            this.state.dispatchEmployeePermission &&
            this.state.dispatchLocations.length > 1;

        const shouldSetEquipmentActive =
            this.state.selectedResourceLocation !== this.state.selectedJobDispatchLocation &&
            this.state.dispatchEquipmentPermission &&
            this.state.dispatchLocations.length > 1;

        if (!this.state.activeResourceTab) {
            if (shouldSetStaffActive) {
                this.setState({ activeResourceTab: staffTab });
            } else if (shouldSetEquipmentActive) {
                this.setState({ activeResourceTab: equipmentTab });
            }
        } else {
            const isStaffActive = this.state.activeResourceTab.name === 'Staff';
            const isEquipmentActive = this.state.activeResourceTab.name === 'Equipment';

            if (isStaffActive && !shouldSetStaffActive) {
                if (shouldSetEquipmentActive) {
                    this.setState({ activeResourceTab: equipmentTab });
                }
            } else if (isEquipmentActive && !shouldSetEquipmentActive) {
                if (shouldSetStaffActive) {
                    this.setState({ activeResourceTab: staffTab });
                }
            }
        }
    }

    onAttendanceClick = () => {
        let { onlyShowAttendance } = this.state;

        onlyShowAttendance = !onlyShowAttendance;

        this.setState({
            onlyShowAttendance,
            onlyShowUnassigned: false,
            onlyShowCancelled: false,
        });
    };

    // #endregion

    // #region DND

    onBeforeDragCapture = (draggable) => {
        // by setting draggable Id in the state here, we can modify the dimensions of the draggable item.
        // this helps us solve the issues when its hard to drop between items on job assignments.
        this.setState({
            draggableId:
                draggable.draggableId /* RLC: keeps track of what we're dragging. */,
        });
    };

    onCancelledClick = () => {
        let { onlyShowCancelled } = this.state;

        onlyShowCancelled = !onlyShowCancelled;

        this.setState({
            onlyShowCancelled,
            onlyShowUnassigned: false,
            onlyShowAttendance: false,
        });
    };

    onChange = onFieldChange;

    setStateAsync = async (state) => {
        await new Promise((resolve) => {
            this.setState(state, resolve);
        });
    };

    // #region RESOURCES
    onCurrentWeekChanged = (selection) => {
        // the week we are changing to
        const week = { ...selection };
        // get user current selection before swapping weeks
        const { selectedWeekRangeDays } = { ...this.state };
        // get the day ints of the selected values
        const daysSelected = selectedWeekRangeDays.map((d) => d.getDay());
        // take the days in the newly selected week and get the corresponding previous day selections
        this.setState({ cancelJobs: [] });
        const newDaySelections = [
            ...week.days
                .filter((wd) => daysSelected.includes(wd.id))
                .map((wd) => new Date(wd.date)),
        ];

        this.setState(
            {
                selectedWeek: week,
                selectedWeekRangeDays: newDaySelections,
            },
            async () => {
                await Promise.all([
                    this.getResources(true),
                    this.getJobs(),
                    this.getCancellations(),
                ]);
            },
        );
    };

    onDragEnd = async (result) => {
        const { source, destination } = result;

        // Reset drag state
        this.setState({
            dragging: false,
            draggableId: null,
            draggedResource: null,
        });

        if (!destination || source.droppableId === destination.droppableId) {
            return;
        }

        const [dragType, objectId] = result.draggableId.split('_');
        const [dropType, jobIdx, dayId, slotIdx] = destination.droppableId.split('_');

        const sourceData = this.getCurrentResource().rows.find((r) => r.id === parseFloat(objectId));

        if (!sourceData) {
            return;
        }

        // Process daily attendance events first
        if (dropType === 'dailyAttendanceEvent') {
            if (dragType !== DraggableResourceTypes.Staff) {
                showCollectedErrors(['Not Supported: Only Staff may be assigned to a Daily Attendance Event.']);

                return;
            }

            await this.updateEmployeeDailyAttendanceEvent(sourceData.id, jobIdx);
            return;
        }

        const allErrors = [];
        const allWarnings = [];
        let items = [];
        const restrictions = [];
        const certificationValidations = [];

        const isBulk = this.state.isCtrlPressed;
        const bulkDays = this.state.selectedWeekRangeDays;

        const snapshot = { ...this.state };

        // For grouped jobs, the baseJob here represents the parent of the group
        // A reference to the actual job being updated is stored on the slot
        const baseJob = snapshot.planningJobs.find((j) => j.id === parseFloat(jobIdx));
        const dayIndex = parseInt(dayId, 10);

        const slot = baseJob.assignments[dayIndex][dragType][+slotIdx];
        const weekStart = new Date(snapshot.selectedWeek.start);
        const assignDate = new Date(snapshot.selectedWeek.days.find((d) => d.id == dayId).date);

        // For grouped jobs, each slot may have its own jobId
        // We want to make sure that we use the actual jobId for all operations, rather than the parent
        const baseItem = makeAssignmentItem({
            jobId: slot.jobId,
            resourceId: slot.resourceId,
            weekOfDate: weekStart,
            assignmentDate: assignDate,
            day: dayIndex,
            employeeId: dragType === DraggableResourceTypes.Staff ? sourceData.id : undefined,
            equipmentId: dragType === DraggableResourceTypes.Equipment ? sourceData.id : undefined,
            isRentalPickup: slot.isRentalPickup,
        });

        // If not bulk (Ctrl not pressed), only process the current day
        const daysToProcess = isBulk ? bulkDays : [assignDate];

        // Always iterate over the selected week-range days and attempt assignment
        daysToProcess.forEach((dayObj) => {
            const dayInt = dayObj.getDay();

            // Check for employee restrictions first
            if (dragType === DraggableResourceTypes.Staff) {
                const msg = getEmployeeRestrictionMessage(sourceData, baseJob, dayObj);

                if (msg) {
                    if (isBulk) {
                        restrictions.push(msg);
                    } else {
                        allErrors.push(msg);
                        items.length = 0;
                    }

                    return;
                }

                const staffAssignments = baseJob.assignments?.[dayInt]?.staff ?? [];
                const empToEmpMsg = getEmployeeToEmployeeRestrictionMessage(
                    sourceData, staffAssignments, snapshot.staff?.rows, dayObj,
                );

                if (empToEmpMsg) {
                    if (isBulk) {
                        restrictions.push(empToEmpMsg);
                    } else {
                        allErrors.push(empToEmpMsg);
                        items.length = 0;
                    }

                    return;
                }

                const timeRestrictionMsg = getTimeBasedRestrictionMessage(sourceData, baseJob, dayObj);

                if (timeRestrictionMsg) {
                    if (isBulk) {
                        restrictions.push(timeRestrictionMsg);
                    } else {
                        allErrors.push(timeRestrictionMsg);
                        items.length = 0;
                    }

                    return;
                }

                const employeeCertificationValidationMessage = getCertificationValidationMessage(sourceData, dayObj);

                if (employeeCertificationValidationMessage) {
                    if (isBulk) {
                        certificationValidations.push(employeeCertificationValidationMessage);
                    } else {
                        allErrors.push(employeeCertificationValidationMessage);
                        items.length = 0;
                    }

                    return;
                }
            }

            const slotDay = baseJob.assignments?.[dayInt]?.[dragType]?.[+slotIdx];

            if (!slotDay?.resourceId) {
                return;
            }

            const candidate = makeAssignmentItem({
                ...baseItem,
                jobId: slotDay.jobId,
                resourceId: slotDay.resourceId,
                assignmentDate: dayObj,
                day: dayInt,
            });

            const { isValidItem, errors, warnings } = this.canResourceBeAssigned(
                dragType,
                sourceData,
                dragType === DraggableResourceTypes.Equipment
                    ? baseJob.assignments[dayInt].equipment
                    : baseJob.assignments[dayInt].staff,
                slotDay,
                baseJob,
                candidate,
                dragType === DraggableResourceTypes.Equipment
                    ? snapshot.planningJobs.flatMap((j) => j.assignments[dayInt].equipment)
                    : snapshot.planningJobs.flatMap((j) => j.assignments[dayInt].staff),
            );

            allErrors.push(...errors);
            allWarnings.push(...warnings);

            if (!isValidItem) {
                return;
            }

            items.push(candidate);

            // Auto-add linked equipment rows
            const linkedEquipment = getLinkedEquipmentCandidates(sourceData, dragType);

            if (linkedEquipment.length) {
                const sameLoc = this.state.selectedJobDispatchLocation === this.state.selectedResourceLocation;
                const canDispatch = this.state.dispatchEquipmentPermission;

                if (sameLoc || canDispatch) {
                    const depSlots = baseJob.assignments[dayInt].equipment.filter(
                        (s) =>
                            !s.assignment &&
                            s.requiredEquipment.some((req) =>
                                linkedEquipment
                                    .map((e) => e.equipmentTypeId)
                                    .includes(req.id),
                            ),
                    );

                    const usedDeps = [];
                    depSlots.forEach((depSlot) => {
                        const eq = linkedEquipment.find(
                            (e) =>
                                !usedDeps.includes(e.id) &&
                                depSlot.requiredEquipment.some(
                                    (r) => r.id === e.equipmentTypeId,
                                ),
                        );

                        if (!eq) {
                            return;
                        }

                        // Create candidate equipment assignment
                        const equipmentCandidate = makeAssignmentItem({
                            jobId: depSlot.jobId,
                            resourceId: depSlot.resourceId,
                            weekOfDate: weekStart,
                            assignmentDate: dayObj,
                            day: dayInt,
                            equipmentId: eq.id,
                            equipmentEmployeeAssignmentId: eq.equipmentEmployeeAssignmentId,
                            equipmentAssignedEquipmentId: eq.equipmentAssignedEquipmentId,
                            isRentalPickup: depSlot.isRentalPickup,
                        });

                        // Validate the equipment assignment
                        const { isValidItem, errors, warnings } = this.canEquipmentBeAssigned(
                            eq,
                            baseJob.assignments[dayInt].equipment,
                            depSlot,
                            baseJob,
                            equipmentCandidate,
                            snapshot.planningJobs.flatMap((j) => j.assignments[dayInt].equipment),
                        );

                        if (warnings.length) {
                            allWarnings.push(...warnings);
                        }

                        if (isValidItem) {
                            items.push(equipmentCandidate);
                            usedDeps.push(eq.id);
                        } else if (errors.length) {
                            allWarnings.push(...errors);
                        }
                    });
                }
            }
        });

        // Bulk mode: if some days committed and others were skipped due to restrictions,
        // surface skipped days as a warning rather than blocking the whole batch.
        const hasItemsToCommit = items.length > 0;

        if (isBulk && hasItemsToCommit) {
            if (restrictions.length) {
                allWarnings.push(...restrictions);
            }
            if (certificationValidations.length) {
                allWarnings.push(...certificationValidations);
            }
        } else {
            if (restrictions.length) {
                allErrors.push(...restrictions);
            }
            if (certificationValidations.length) {
                allErrors.push(...certificationValidations);
            }
        }

        if (allErrors.length > 0) {
            showCollectedErrors(allErrors);
            return;
        }

        if (!items.length) {
            if (allWarnings.length) {
                showCollectedWarnings(allWarnings);
            }
            return;
        }

        let selectedId = null;
        let selectedType = null;

        if (dragType === DraggableResourceTypes.Equipment && sourceData && items.length > 0) {
            const equipmentType = sourceData?.equipmentType;

            if (equipmentType) {
                const dayIndex = parseInt(dayId, 10);

                // Find ALL equipment items for this equipment (could be multiple days in bulk assignment)
                const equipmentItems = items.filter((item) => item.assignedEquipmentId === sourceData.id);
                const needsAssignment = equipmentItems.some((item) => !item.equipmentEmployeeAssignmentId && !item.equipmentAssignedEquipmentId);

                if (needsAssignment) {
                    const pendingStaffAssignments = items
                        .filter((item) => item.assignedEmployeeId && !item.assignedEquipmentId)
                        .map((item) => {
                            const staffFromResources = this.getCurrentResource().rows.find((s) => s.id === item.assignedEmployeeId);
                            return staffFromResources ? {
                                id: item.assignedEmployeeId,
                                ...staffFromResources,
                            } : null;
                        })
                        .filter((s) => s !== null); // Filter out any that weren't found

                    // Units auto-added onto the dragged equipment can't also be its host
                    const pendingEquipmentAssignments = items
                        .filter((item) => item.assignedEquipmentId
                            && item.assignedEquipmentId !== sourceData.id
                            && item.equipmentAssignedEquipmentId !== sourceData.id)
                        .map((item) => {
                            const equipFromResources = this.state.equipmentResources.find((e) => e.id === item.assignedEquipmentId);
                            return equipFromResources ? {
                                id: item.assignedEquipmentId,
                                ...equipFromResources,
                            } : null;
                        })
                        .filter((e) => e !== null);

                    const currentDayAssignments = baseJob.assignments[dayIndex];
                    const augmentedDayAssignments = {
                        ...currentDayAssignments,
                        staff: [
                            ...currentDayAssignments.staff,
                            ...pendingStaffAssignments.map((staff) => ({
                                assignment: staff,
                            })),
                        ],
                        equipment: [
                            ...currentDayAssignments.equipment,
                            ...pendingEquipmentAssignments.map((equip) => ({
                                assignment: equip,
                            })),
                        ],
                    };

                    const equipmentSlot = baseJob.assignments[dayIndex].equipment[+slotIdx];

                    const requiredEquipmentEntry = equipmentSlot?.requiredEquipment?.find(
                        (req) => req.id === sourceData.equipmentTypeId,
                    );

                    const canAssignToStaff = requiredEquipmentEntry?.employeeAssignableQuantity != null
                        && requiredEquipmentEntry.employeeAssignableQuantity > 0;
                    const canAssignToEquipment = requiredEquipmentEntry?.equipmentAssignableQuantity != null
                        && requiredEquipmentEntry.equipmentAssignableQuantity > 0;

                    let eligibleStaff = [];
                    let eligibleEquipment = [];
                    let eligibleResources = [];
                    let resourceType = null;

                    if (canAssignToEquipment) {
                        eligibleEquipment = getEligibleEquipmentForEquipment(
                            sourceData,
                            requiredEquipmentEntry || equipmentType,
                            augmentedDayAssignments,
                        );
                    }

                    if (canAssignToStaff) {
                        eligibleStaff = getEligibleStaffForEquipment(
                            sourceData,
                            requiredEquipmentEntry || equipmentType,
                            augmentedDayAssignments,
                        );
                    }

                    const totalEligibleCount = eligibleStaff.length + eligibleEquipment.length;

                    if (totalEligibleCount === 1) {
                        if (eligibleEquipment.length === 1) {
                            selectedId = eligibleEquipment[0].id;
                            selectedType = DraggableResourceTypes.Equipment;
                        } else if (eligibleStaff.length === 1) {
                            selectedId = eligibleStaff[0].id;
                            selectedType = DraggableResourceTypes.Staff;
                        }
                    } else if (totalEligibleCount > 1) {
                        if (eligibleStaff.length > 0 && eligibleEquipment.length > 0) {
                            resourceType = DraggableResourceTypes.Staff;
                        } else if (eligibleEquipment.length > 0) {
                            eligibleResources = eligibleEquipment;
                            resourceType = DraggableResourceTypes.Equipment;
                        } else {
                            eligibleResources = eligibleStaff;
                            resourceType = DraggableResourceTypes.Staff;
                        }

                        const result = await Prompt.ask({
                            title: 'Assign Equipment',
                            SaveButtonProps: {
                                label: 'Assign',
                            },
                            initialValues: {
                                resourceTypeSelection: resourceType,
                                selectedId: '',
                            },
                            body: ({ values, setFieldValue }) => (
                                <EquipmentAssignmentModal
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    resources={eligibleResources}
                                    resourceType={resourceType}
                                    equipment={sourceData}
                                    eligibleStaff={eligibleStaff}
                                    eligibleEquipment={eligibleEquipment}
                                />
                            ),
                        });

                        if (!result) {
                            return;
                        }

                        selectedId = result.selectedId;
                        selectedType = result.resourceTypeSelection;
                    }
                }
            }
        }

        if (selectedId && selectedType && sourceData) {
            items = items.map((item) => {
                if (item.assignedEquipmentId !== sourceData.id) {
                    return item;
                }

                if (selectedType === DraggableResourceTypes.Staff) {
                    return { ...item, equipmentEmployeeAssignmentId: selectedId, equipmentAssignedEquipmentId: null };
                }

                if (selectedType === DraggableResourceTypes.Equipment) {
                    return { ...item, equipmentAssignedEquipmentId: selectedId, equipmentEmployeeAssignmentId: null };
                }

                return item;
            });
        }

        const jobIds = uniq([...items.map((i) => i.jobId), baseJob.id]);
        await this.setJobSaving(jobIds);

        let rsp;
        try {
            rsp = await this.saveAssignment(items);
        } finally {
            await this.setJobNotSaving(jobIds);
        }

        if (!rsp || !rsp.successDates?.length) {
            const responseErrors = rsp?.errorMessages ?? ['Failed to save assignment.'];

            showCollectedErrors([...allErrors, ...responseErrors]);
            return;
        }

        // Show warnings and any API response warnings (including cross-office double-booking warnings)
        if (allWarnings.length || rsp.errorMessages?.length || rsp.warningMessages?.length) {
            const allFinalWarnings = [...allWarnings];
            if (rsp.errorMessages?.length) {
                allFinalWarnings.push(...rsp.errorMessages);
            }
            if (rsp.warningMessages?.length) {
                allFinalWarnings.push(...rsp.warningMessages);
            }
            showCollectedWarnings(allFinalWarnings);
        }

        if (destination.droppableId === this.state.slotFilter?.id) {
            this.clearSlotFilter();
        }
    };

    onDragStart = (start) => {
        const [resourceType, resourceId] = start.draggableId.split('_'); // Extract type and id
        this.setState({
            draggedResource: { type: resourceType, id: resourceId }, // Store the resource type and ID together
            dragging: true,
        });

        if (this.state.selectedAttendanceEvent) {
            this.setState({
                selectedAttendanceEvent: null,
                selectedStaffForAssignment: [],
            });
        }
    };

    getDraggedResource = () => {
        const { draggedResource } = this.state;
        const { staff, equipment } = this.state;

        if (!draggedResource) return null;

        if (draggedResource.type === DraggableResourceTypes.Staff) {
            return staff.rows.find((s) => s.id.toString() === draggedResource.id);
        }
        if (draggedResource.type === DraggableResourceTypes.Equipment) {
            return equipment.rows.find((e) => e.id.toString() === draggedResource.id);
        }

        return null;
    };

    // #region WORKFLOW EDITS
    onEditJobWorkflowClicked = (job, day) => {
        // Validation: if crew lead timesheets and staff are assigned, make sure there is a crew lead.
        const dayAssignments = { ...(job.assignments?.[day] ?? {}) };

        const selWfJob = { job, day, assignments: dayAssignments };
        this.context.setFormOpened(true);
        this.setState({
            selectedWorkflowJob: selWfJob,
            jobWorkflowModalOpen: true,
            isCancelledJobWorkflow: false,
        });
    };

    onViewJobWorkflowClicked = (job, day) => {
        const dayAssignments = { ...(job.assignments?.[day] ?? {}) };

        const selWfJob = { job, day, assignments: dayAssignments };
        this.context.setFormOpened(true);
        this.setState({
            selectedWorkflowJob: selWfJob,
            jobWorkflowModalOpen: true,
            isCancelledJobWorkflow: true,
        });
    };

    onFormCompletionChange = (jobId, completed) => {
        this.setState((prev) => ({
            allJobs: prev.allJobs.map((j) => (j.id === jobId ? { ...j, formsCompleted: completed } : j)),
            planningJobs: prev.planningJobs.map((j) => (j.id === jobId ? { ...j, formsCompleted: completed } : j)),
        }));
    };

    onEquipmentAssignmentChange = async (
        jobId,
        dayIndex,
        assignmentType,
        assignmentIndex,
        resourceId,
    ) => {
        const { planningJobs } = { ...this.state };

        const job = planningJobs.find((j) => j.id === jobId);
        if (!job) return;

        const jobAssignment = {
            ...job.assignments[dayIndex].equipment[assignmentIndex],
        };

        await this.setJobSaving(jobId);

        try {
            if (assignmentType === DraggableResourceTypes.Staff) {
                await httpClient.js(
                    ApiRoutes.dispatch.setEquipmentAssignedEmployee(
                        jobAssignment.jobAssignmentId,
                        resourceId,
                    ),
                );
            } else {
                await httpClient.js(
                    ApiRoutes.dispatch.setEquipmentAssignedEquipment(
                        jobAssignment.jobAssignmentId,
                        resourceId,
                    ),
                );
            }
        } finally {
            await this.setJobNotSaving(jobId);
        }
    };
    // #endregion

    onJobCancellationUpdatedFromHub = (jobUpdatedString) => {
        const { selectedJobDispatchLocation, cancellationJobs, crossDispatchCancellationJobs } = { ...this.state };
        const updatedJob = JSON.parse(jobUpdatedString);

        if (updatedJob.dispatchLocationId !== selectedJobDispatchLocation) {
            return;
        }

        const existingJob = cancellationJobs.find(
            (j) => j.id === updatedJob.id,
        );
        const existingCrossJob = crossDispatchCancellationJobs.find(
            (j) => j.id === updatedJob.id,
        );
        let i = null;
        if (existingJob) i = cancellationJobs.findIndex((x) => x.id === existingJob.id);

        let c = null;
        if (existingCrossJob) c = crossDispatchCancellationJobs.findIndex((x) => x.id === existingCrossJob.id);

        if (i == null || i == -1) {
            cancellationJobs.push(updatedJob);
        } else {
            cancellationJobs[i] = updatedJob;
        }

        if (c == null || c == -1) {
            crossDispatchCancellationJobs.push(updatedJob);
        } else {
            crossDispatchCancellationJobs[c] = updatedJob;
        }

        const message = `Jobs: ${updatedJob.name} - ${updatedJob.number} - ${updatedJob.startTime} were cancelled.`;
        this.setState(
            {
                cancellationJobs,
                crossDispatchCancellationJobs,
                lastPushUpdate: message,
            },
            () => {
                this.setJobNotSaving(updatedJob.id);
            },
        );
    };

    onJobCreatedFromHub = (jobCreatedString, data) => {
        const { selectedJobDispatchLocation } = this.state;
        const job = typeof jobCreatedString === 'string' ? JSON.parse(jobCreatedString) : jobCreatedString;

        const allJobs = data ?? this.state.allJobs;

        if (job.dispatchLocationId !== selectedJobDispatchLocation) {
            return allJobs;
        }

        const allUpdated = [...allJobs, job];

        const message = `Jobs: ${job.name} - ${job.number} - ${job.startTime} was added.`;

        this.setState({
            lastPushUpdate: message,
        });

        if (!data) {
            this.getJobs(allUpdated);
        }

        return allUpdated;
    };

    onJobTabClicked = (tab) => {
        const selectedTab = { ...tab };
        this.setState({
            activeJobTab: selectedTab, selectedPlanningStatus: null, selectedCancellationStatus: null, selectedEventStatus: null,
        });
    };

    onJobUpdatedFromHub = (jobUpdatedString) => {
        const { selectedJobDispatchLocation, selectedWeekRangeDays } = this.state;

        const updatedJob = JSON.parse(jobUpdatedString);

        if (updatedJob.dispatchLocationId !== selectedJobDispatchLocation) {
            return;
        }

        const { planningJobs, resourceJobs } = this.state;
        const existingJob = planningJobs.find((j) => parseFloat(j.id) === parseFloat(updatedJob.id) || j.jobIdsInGroup?.includes(updatedJob.id));
        const i = planningJobs.indexOf(existingJob);

        const ir = resourceJobs.findIndex((j) => parseFloat(j.id) === parseFloat(updatedJob.id) || j.jobIdsInGroup?.includes(updatedJob.id));

        // Need to update allJobs too since in the update case, we're replacing the job
        // with a new instance.
        const { allJobs } = this.state;
        const ia = allJobs.indexOf(existingJob);

        let cancelled = false;
        if (selectedWeekRangeDays.length == 1) {
            cancelled = selectedWeekRangeDays.every(
                (x) => updatedJob.assignments[x.getDay()].equipment.length == 0
                    && updatedJob.assignments[x.getDay()].staff.length == 0,
            );
        }

        if (updatedJob.isDeleted || cancelled) {
            if (i !== -1) {
                planningJobs.splice(i, 1);
            }
            if (ia !== -1) {
                allJobs.splice(ia, 1);
            }
            if (ir !== -1) {
                resourceJobs.splice(ir, 1);
            }
        } else {
            const existingOverrides = existingJob?.overrides || [];
            updatedJob.overrides = existingOverrides;
            updatedJob.hasCustomForms = existingJob?.hasCustomForms ?? false;
            updatedJob.formsCompleted = existingJob?.formsCompleted ?? true;
            planningJobs[i] = updatedJob;
            allJobs[ia] = updatedJob;

            if (ir !== -1) {
                resourceJobs[ir] = updatedJob;
            } else {
                resourceJobs.push(updatedJob);
            }
        }

        const action = updatedJob.isDeleted ? 'deleted' : 'updated';

        const message = `Jobs: ${updatedJob.name} - ${updatedJob.number} - ${updatedJob.startTime} was ${action}.`;
        this.setState(
            {
                allJobs,
                planningJobs,
                resourceJobs,
                lastPushUpdate: message,
            },
        );
    };

    onJobsUpdatedBulkFromHub = (jsonStr, data) => {
        const jobs = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
        const allJobs = data ?? this.state.allJobs;

        const allUpdated = allJobs
            .map((existing) => {
                const updated = jobs.find(
                    (j) => parseFloat(j.id) === parseFloat(existing.id),
                );

                if (!updated) return existing;

                return {
                    ...existing,
                    ...updated,
                    overrides: existing.overrides || updated.overrides || [],
                    hasCustomForms: existing.hasCustomForms ?? false,
                    formsCompleted: existing.formsCompleted ?? true,
                };
            })
            .filter((x) => !x.isDeleted);

        const resourceUpdated = this.state.resourceJobs
            .map((rj) => {
                const updated = jobs.find((j) => parseFloat(j.id) === parseFloat(rj.id));
                return updated || rj;
            })
            .filter((x) => !x.isDeleted);

        const planningUpdated = allUpdated.filter(this.isFilteredPlanningJob);
        const message = `${jobs.length} jobs updated.`;

        this.setState({
            lastPushUpdate: message,
            allJobs: allUpdated,
            planningJobs: planningUpdated,
            planningJobsFilteredCount: planningUpdated.length,
            planningJobsMaxRows: allUpdated.length,
            resourceJobs: resourceUpdated,
        });

        if (!data) {
            this.getJobs(allUpdated);
        }

        return allUpdated;
    };

    onJobDeletedFromHub = (jobDeletedString, data) => {
        const allJobs = data ?? this.state.allJobs;

        const deletedJobId = typeof jobDeletedString === 'string' ? JSON.parse(jobDeletedString) : jobDeletedString;
        const allUpdated = allJobs.filter((job) => parseFloat(job.id) !== parseFloat(deletedJobId));

        if (!data) {
            this.getJobs(allUpdated);
        }

        return allUpdated;
    };

    onUpdateMultipleJobs = (jobUpdatedString) => {
        let { allJobs } = this.state;
        const batch = JSON.parse(jobUpdatedString);

        for (const job of batch.created || []) {
            allJobs = this.onJobCreatedFromHub(job, allJobs);
        }

        if (!isEmpty(batch.updated)) {
            allJobs = this.onJobsUpdatedBulkFromHub(batch.updated, allJobs);
        }

        for (const job of batch.deleted || []) {
            allJobs = this.onJobDeletedFromHub(job, allJobs);
        }

        this.getJobs(allJobs);
    };

    onNextWeekClicked = () => {
        const { weeks, selectedWeek } = { ...this.state };
        let currentIndex = weeks
            .map((x) => x.start)
            .indexOf(selectedWeek.start);
        if (currentIndex + 1 === weeks.length) return;
        this.onCurrentWeekChanged({ ...weeks[(currentIndex += 1)] });
    };

    onNoResponseCheckedChanged = (e) => {
        let { noResponse } = this.state;
        noResponse = e.target.checked;
        this.setState({ noResponse });
    };

    onPreviousWeekClicked = () => {
        const { weeks, selectedWeek } = { ...this.state };
        let currentIndex = weeks
            .map((x) => x.start)
            .indexOf(selectedWeek.start);
        if (currentIndex === 0) return;
        this.onCurrentWeekChanged({ ...weeks[(currentIndex -= 1)] });
    };

    showPreferredDispatch() {
        const { dispatchLocations } = this.state;
        const { user } = this.context;

        return (dispatchLocations?.length > 1) || user.isAdmin;
    }

    onRefreshDayJobs = async () => {
        const [_, allData, __] = await Promise.all([
            this.getResources(true),
            this.getAllJobs(),
            this.getCancellations(),
        ]);

        const pjs = allData.filter(this.isFilteredPlanningJob);

        this.setState({
            allJobs: allData,
            planningJobs: pjs,
            planningJobsFilteredCount: pjs.length,
            planningJobsMaxRows: allData.length,
        });

        toasty.success('Refresh successful.');
    };

    onResourceTabClicked = (selectedTab) => {
        const { activeResourceTab } = this.state;

        if (selectedTab !== activeResourceTab) {
            this.clearSlotFilter();
            this.resetSavedResourceFilters();

            this.setState({
                activeResourceTab: selectedTab,
                selectedAttendanceEvent: null,
                selectedStaffForAssignment: [],
            }, async () => {
                await this.handleResourceSearchText('');
            });
        }
    };

    onSelectedResourceLocationChanged = (selection) => {
        const isAdmin = this.context?.user?.isAdmin;
        const loc = (this.state.dispatchLocations ?? []).find(
            (x) => x.value === selection.value,
        ) ?? '';

        this.setState({
            selectedResourceLocation: loc.value,
            resourceLocation: loc.value,
            resourceSearchText: '',
        }, async () => {
            this.handleDispatchLocationChange();

            if (!isAdmin) {
                await Promise.all([this.getResources(true), this.getJobs()]);
            } else {
                await this.getResources(true);
            }
        });
    };

    onSelectedJobDispatchLocationChanged = (selection) => {
        const { selectedResourceDispatchLocation } = { ...this.state };
        const isAdmin = this.context?.user?.isAdmin;
        const loc = { ...selection };

        this.setState({
            selectedJobDispatchLocation: loc.value,
            selectedResourceLocation: loc.value,
            resourceSearchText: '',

            // #2180 - Admins can cross dispatch.  Otherwise, keep the panes (resources/jobs)
            //        in dispatch step by setting the selected dispatch on both sides.
            selectedResourceDispatchLocation:
                isAdmin
                    ? selectedResourceDispatchLocation
                    : loc.value,
        }, async () => {
            this.handleDispatchLocationChange();

            await Promise.all([this.getResources(true), this.getJobs(), this.getCancellations()]);
        });
    };

    handleMobileDateNavigation = (direction) => {
        const { selectedWeekRangeDays, selectedWeek } = this.state;
        if (!selectedWeekRangeDays || selectedWeekRangeDays.length === 0 || !selectedWeek) return;

        // Compute daysInRange the same way as in render
        const daysInRange = getDaysInRange(
            moment(selectedWeek.start).toDate(),
            moment(selectedWeek.end).toDate(),
        );

        const [currentDate] = selectedWeekRangeDays;
        const currentIndex = daysInRange.findIndex((day) =>
            day.toLocaleDateString() === currentDate.toLocaleDateString(),
        );

        const newIndex = currentIndex + direction;

        // Check bounds
        if (newIndex >= 0 && newIndex < daysInRange.length) {
            const newDate = daysInRange[newIndex];

            // Update state with only the new single date
            this.setState({
                selectedWeekRangeDays: [newDate],
            }, async () => {
                await Promise.all([
                    this.getJobs(),
                    this.getCancellations(),
                    this.getResources(true),
                ]);
            });
        }
    };

    handleMobileDateSelection = (date) => {
        if (!date) return;

        const { weeks, selectedWeek } = this.state;

        // Find the week that contains the selected date
        const selectedMoment = moment(date);
        const targetWeek = weeks.find((week) => {
            const weekStart = moment(week.start);
            const weekEnd = moment(week.end);
            return selectedMoment.isSameOrAfter(weekStart) && selectedMoment.isSameOrBefore(weekEnd);
        });

        if (!targetWeek) {
            // Date is outside of available weeks - create a custom week for this date
            const selectedDate = moment(date);
            const weekStart = selectedDate.clone().startOf('week').add(1, 'day'); // Monday
            const weekEnd = weekStart.clone().add(6, 'days'); // Sunday

            const customWeekDays = Array.from({ length: 7 }, (_, i) => {
                const d = weekStart.clone().add(i, 'days');
                return { id: d.day(), date: d.toISOString() };
            });

            const customWeek = {
                start: weekStart.toISOString(),
                end: weekEnd.toISOString(),
                startDisplay: weekStart.format('M/D/YYYY'),
                endDisplay: weekEnd.format('M/D/YYYY'),
                current: false,
                days: customWeekDays,
            };

            // Update state with the custom week and selected date
            this.setState({
                selectedWeek: customWeek,
                selectedWeekRangeDays: [date],
                cancelJobs: [],
            }, async () => {
                await Promise.all([
                    this.getResources(true),
                    this.getJobs(),
                    this.getCancellations(),
                ]);
            });

            return;
        }

        // If the selected date is in a different week, change the week
        if (targetWeek.start !== selectedWeek.start) {
            // First change the week
            this.setState({
                selectedWeek: targetWeek,
                selectedWeekRangeDays: [date],
                cancelJobs: [],
            }, async () => {
                await Promise.all([
                    this.getResources(true),
                    this.getJobs(),
                    this.getCancellations(),
                ]);
            });
        } else {
            // Same week, just update the selected day
            this.setState({
                selectedWeekRangeDays: [date],
            }, async () => {
                await Promise.all([
                    this.getJobs(),
                    this.getCancellations(),
                    this.getResources(),
                ]);
            });
        }
    };

    canNavigateMobile = (direction) => {
        const { selectedWeekRangeDays, selectedWeek } = this.state;
        if (!selectedWeekRangeDays || selectedWeekRangeDays.length === 0 || !selectedWeek) return false;

        // Compute daysInRange the same way as in handleMobileDateNavigation
        const daysInRange = getDaysInRange(
            moment(selectedWeek.start).toDate(),
            moment(selectedWeek.end).toDate(),
        );

        const [currentDate] = selectedWeekRangeDays;
        const currentIndex = daysInRange.findIndex((day) =>
            day.toLocaleDateString() === currentDate.toLocaleDateString(),
        );

        const newIndex = currentIndex + direction;

        // Check if the new index is within bounds
        return newIndex >= 0 && newIndex < daysInRange.length;
    };

    // Mobile assignment handlers
    openMobileAssignmentModal = (resourceType, job, day, slot, slotIndex) => {
        // Auto-select resource if there's only one eligible resource
        const autoSelectedResource = resourceType === DraggableResourceTypes.Equipment &&
            slot?.eligibleResources?.length === 1
            ? slot.eligibleResources[0]
            : null;

        this.setState({
            showMobileAssignmentModal: true,
            mobileAssignmentResourceType: resourceType,
            mobileAssignmentSlot: slot,
            mobileAssignmentSlotIndex: slotIndex,
            mobileAssignmentJob: job,
            mobileAssignmentDay: day,
            mobileSelectedResource: null,
            mobileSelectedStaffForEquipment: autoSelectedResource,
        });
    };

    closeMobileAssignmentModal = () => {
        this.setState({
            showMobileAssignmentModal: false,
            mobileAssignmentResourceType: null,
            mobileAssignmentSlot: null,
            mobileAssignmentSlotIndex: null,
            mobileAssignmentJob: null,
            mobileAssignmentDay: null,
            mobileSelectedResource: null,
            mobileIsBulkAssignment: false,
            mobileSelectedStaffForEquipment: null,
        });
    };

    onMobileResourceSelect = (resource) => {
        this.setState({
            mobileSelectedResource: resource,
        });
    };

    onMobileBulkAssignmentToggle = (value) => {
        this.setState({
            mobileIsBulkAssignment: value,
        });
    };

    assignEquipmentToStaff = async (jobId, dayIndex, equipmentSlotIndex, staffId) => {
        const { planningJobs } = this.state;
        const job = planningJobs.find((j) => j.id === jobId);
        if (!job) return;

        const equipmentSlot = job.assignments[dayIndex].equipment[equipmentSlotIndex];
        if (!equipmentSlot) return;

        await this.onEquipmentAssignmentChange(
            jobId,
            dayIndex,
            DraggableResourceTypes.Staff, // assignmentType
            equipmentSlotIndex,
            staffId,
        );
    };

    onMobileSaveAssignment = async () => {
        const {
            mobileSelectedResource,
            mobileAssignmentSlot,
            mobileAssignmentSlotIndex,
            mobileAssignmentJob,
            mobileAssignmentDay,
            mobileAssignmentResourceType,
            selectedWeek,
            mobileIsBulkAssignment,
            mobileSelectedStaffForEquipment,
        } = this.state;

        if (!mobileSelectedResource || !mobileAssignmentSlot) return;

        const weekStart = new Date(selectedWeek.start);
        const assignDate = new Date(selectedWeek.days.find((d) => d.id == mobileAssignmentDay).date);

        // Determine which days to process based on bulk assignment toggle
        const daysToProcess = mobileIsBulkAssignment
            ? selectedWeek.days.map((day) => new Date(day.date))
            : [assignDate];
        const assignmentItems = [];
        const allErrors = [];
        const allWarnings = [];

        // Find all jobs in the same group (similar to desktop bulk assignment)
        const jobsInGroup = (groupJobsByParent(this.state.planningJobs)
            .find((g) => g.some((j) => j.id === mobileAssignmentJob.id || (!!mobileAssignmentJob.parentJobId && j.parentJobId === mobileAssignmentJob.parentJobId)))
        ) ?? [mobileAssignmentJob];

        // Create assignment items for each day
        daysToProcess.forEach((dayObj) => {
            const targetDay = dayObj instanceof Date ? dayObj : new Date(dayObj);
            const targetDayIndex = targetDay.getDay();

            // Find the job for this specific day
            const jobForDay = jobsInGroup.find((j) => j.assignments?.[targetDayIndex]?.canEdit);
            if (!jobForDay) {
                return;
            }

            // For bulk assignments, find the slot for this specific day using the slot index
            const slotForDay = jobForDay.assignments[targetDayIndex]?.[mobileAssignmentResourceType]?.[mobileAssignmentSlotIndex];
            if (!slotForDay?.resourceId) {
                return;
            }
            // Skip if slot already has an assignment
            if (slotForDay.assignment) {
                return;
            }

            const assignmentItem = makeAssignmentItem({
                jobId: jobForDay.id,
                resourceId: slotForDay.resourceId,
                weekOfDate: weekStart,
                assignmentDate: targetDay,
                day: targetDayIndex,
                employeeId: mobileAssignmentResourceType === DraggableResourceTypes.Staff ? mobileSelectedResource.id : undefined,
                equipmentId: mobileAssignmentResourceType === DraggableResourceTypes.Equipment ? mobileSelectedResource.id : undefined,
                equipmentEmployeeAssignmentId: mobileAssignmentResourceType === DraggableResourceTypes.Equipment && mobileSelectedStaffForEquipment?.id ? mobileSelectedStaffForEquipment.id : undefined,
                isRentalPickup: slotForDay.isRentalPickup,
            });

            const dayAssignmentsForResource = jobForDay.assignments[targetDayIndex][mobileAssignmentResourceType];
            const assignmentsForAllJobsForTheDay = this.state.planningJobs.flatMap(
                (j) => j.assignments[targetDayIndex]?.[mobileAssignmentResourceType] || [],
            );
            const { isValidItem, errors, warnings } = this.canResourceBeAssigned(
                mobileAssignmentResourceType,
                mobileSelectedResource,
                dayAssignmentsForResource,
                slotForDay,
                jobForDay,
                assignmentItem,
                assignmentsForAllJobsForTheDay,
            );

            allErrors.push(...errors);
            allWarnings.push(...warnings);

            if (!isValidItem) {
                if (mobileIsBulkAssignment && (errors?.length || warnings?.length)) {
                    allWarnings.push(`${moment(targetDay).format('ddd M/D')}: validation failed; skipped.`);
                }
                return;
            }

            assignmentItems.push(assignmentItem);

            // Auto-add linked equipment rows (same as desktop drag and drop)
            const linkedEquipment = getLinkedEquipmentCandidates(mobileSelectedResource, mobileAssignmentResourceType);

            if (linkedEquipment.length) {
                const sameLoc = this.state.selectedJobDispatchLocation === this.state.selectedResourceLocation;
                const canDispatch = this.state.dispatchEquipmentPermission;
                if (sameLoc || canDispatch) {
                    const dayAssignments = jobForDay.assignments[targetDayIndex];
                    if (!dayAssignments || !dayAssignments.equipment) {
                        return;
                    }

                    const depSlots = dayAssignments.equipment.filter(
                        (s) =>
                            !s.assignment &&
                            s.requiredEquipment?.some((req) =>
                                linkedEquipment
                                    .map((e) => e.equipmentTypeId)
                                    .includes(req.id),
                            ),
                    );

                    const usedDeps = [];
                    depSlots.forEach((depSlot) => {
                        // Find matching equipment from the resource's linked equipment
                        const eq = linkedEquipment.find(
                            (e) =>
                                !usedDeps.includes(e.id) &&
                                depSlot.requiredEquipment.some(
                                    (r) => r.id === e.equipmentTypeId,
                                ),
                        );
                        if (!eq) return;

                        // Create candidate equipment assignment
                        const equipmentCandidate = makeAssignmentItem({
                            jobId: jobForDay.id,
                            resourceId: depSlot.id,
                            weekOfDate: weekStart,
                            assignmentDate: targetDay,
                            day: targetDayIndex,
                            equipmentId: eq.id,
                            equipmentEmployeeAssignmentId: eq.equipmentEmployeeAssignmentId,
                            equipmentAssignedEquipmentId: eq.equipmentAssignedEquipmentId,
                            isRentalPickup: depSlot.isRentalPickup,
                        });

                        // Validate the equipment assignment
                        const { isValidItem, errors: depErrors, warnings: depWarnings } = this.canEquipmentBeAssigned(
                            eq,
                            dayAssignments.equipment,
                            depSlot,
                            jobForDay,
                            equipmentCandidate,
                            this.state.planningJobs.flatMap((j) => j.assignments[targetDayIndex]?.equipment || []),
                        );

                        if (depWarnings.length) allWarnings.push(...depWarnings);
                        if (isValidItem) {
                            assignmentItems.push(equipmentCandidate);
                            usedDeps.push(eq.id);
                        } else if (depErrors.length) {
                            allWarnings.push(...depErrors);
                        }
                    });
                }
            }
        });

        // Bulk mode: if some days produced valid assignments and others were skipped
        // due to restrictions, surface the skipped days as warnings rather than
        // blocking the whole batch. Non-bulk behavior unchanged.
        if (mobileIsBulkAssignment && assignmentItems.length > 0 && allErrors.length) {
            allWarnings.push(...allErrors);
            allErrors.length = 0;
        }

        if (allErrors.length) {
            showCollectedErrors(allErrors);
            return;
        }

        if (!assignmentItems.length) {
            if (allWarnings.length) {
                showCollectedWarnings(allWarnings);
            }
            return;
        }

        try {
            await this.setJobSaving([mobileAssignmentJob.id]);
            const rsp = await this.saveAssignment(assignmentItems);

            if (rsp && rsp.successDates?.length) {
                this.closeMobileAssignmentModal();

                let message;
                if (mobileIsBulkAssignment) {
                    // Count unique days from the assignment items that were created
                    const uniqueDays = new Set(assignmentItems.map((item) => item.day)).size;
                    message = `Assignment saved for ${uniqueDays} day${uniqueDays > 1 ? 's' : ''}.`;
                } else {
                    message = 'Assignment saved successfully.';
                }

                toasty.success(message);

                // Show any warnings about cross-office double-booking
                if (allWarnings.length || rsp.warningMessages?.length) {
                    showCollectedWarnings([
                        ...allWarnings,
                        ...(rsp.warningMessages ?? []),
                    ]);
                }
            } else {
                const responseErrors = rsp?.errorMessages ?? ['Failed to save assignment.'];
                showCollectedErrors(responseErrors);
            }
        } catch (e) {
            console.error(e);
        } finally {
            await this.setJobNotSaving([mobileAssignmentJob.id]);
        }
    };

    onSendNotifications = async ({ noResponse }) => {
        const {
            selectedWeekRangeDays, selectedJobDispatchLocation,
        } = { ...this.state };
        // can only send if 1 day is selected.  Button should hide but double check

        this.setState({ isSavingJobStatus: true });

        if (
            (selectedWeekRangeDays ?? []).length === 1
            && selectedJobDispatchLocation
        ) {
            // pop up modal to ask if you want to include no response employees
            const model = {
                date: getInputFormat(selectedWeekRangeDays[0]),
                dispatchLocationId: selectedJobDispatchLocation,
                resendNoResponse: noResponse,
            };

            const response = await httpClient.post(
                ApiRoutes.jobAssignment.notify(),
                model,
            );

            if (!(response.errorMessages ?? []).length) {
                await Promise.all([
                    this.getJobs(),
                    this.getCancellations(),
                ]).catch(() => {
                    this.setState({
                        showModal: false,
                        noResponse: false,
                        isSavingJobStatus: false,
                    });
                });
            } else {
                toasty.warning(
                    'Notifications completed with errors',
                    `${response.errorMessages}`,
                    { autoClose: false, closeOnClick: true },
                );
            }
        }

        this.setState({
            showModal: false,
            noResponse: false,
            isSavingJobStatus: false,
        });
    };
    // #endregion

    onStaffRelatedDataUpdatedFromHub = (employeeId) => {
        const { allStaffIds } = this.state;

        if (!allStaffIds.includes(employeeId)) {
            return;
        }
        this.getResources();
    };

    onUnassignedClick = () => {
        let { onlyShowUnassigned } = this.state;

        onlyShowUnassigned = !onlyShowUnassigned;

        this.setState({
            onlyShowUnassigned,
            onlyShowAttendance: false,
            onlyShowCancelled: false,
        });
    };

    onUnassignedEquipmentClick = () => {
        let { onlyShowUnassignedEquipment } = this.state;

        onlyShowUnassignedEquipment = !onlyShowUnassignedEquipment;

        this.setState({ onlyShowUnassignedEquipment });
    };

    getAllJobs = async (forResources = false) => {
        const { selectedWeek, selectedJobDispatchLocation, selectedResourceLocation } = this.state;
        const searchParams = new DispatchJobSearchParams();

        searchParams.weekStartDate = new Date(selectedWeek.start);
        searchParams.weekEndDate = new Date(selectedWeek.end);
        searchParams.dispatchLocation = forResources ? selectedResourceLocation : selectedJobDispatchLocation;

        // MFN 2022-03-14 - We want all jobs for all days.
        // Restore line to this if this is not what we want (will need to get selectedWeekRangeDays from state): selectedWeekRangeDays.map(x => new Date(x).getDay()) ?? [];
        searchParams.selectedDays = null;
        searchParams.searchText = null; // set to jobSearchText from state if you do not want all jobs.

        const allData = await httpClient.post(
            ApiRoutes.dispatch.jobs(),
            searchParams,
        );
        // Don't need filteredCount/maxRows - we handle that as part of the filtering process.
        const { rows } = allData;

        return rows;
    };
    getCancellationBoard = () => {
        const { cancellationJobs, selectedWeekRangeDays } = { ...this.state };
        const accumulator = { cancelPending: 0, cancelConfirmed: 0 };
        const days = selectedWeekRangeDays.map((wrd) => wrd.getDay());
        const staff = cancellationJobs.reduce(
            (acc, job) => {
                const cancelPending = acc.cancelPending
                    + days.reduce((acc, day) => (
                        acc
                        + job.assignments[day].cancelledAssignments.filter(
                            (g) => !!g.assignedEmployee && !g.confirmedOn,
                        ).length
                    ), 0);
                const cancelConfirmed = acc.cancelConfirmed
                    + days.reduce((acc, day) => (
                        acc
                        + job.assignments[day].cancelledAssignments.filter(
                            (g) => !!g.assignedEmployee && !!g.confirmedOn,
                        ).length
                    ), 0);
                return {
                    cancelPending,
                    cancelConfirmed,
                };
            },
            { ...accumulator },
        );

        const eq = cancellationJobs.reduce(
            (acc, job) => {
                const cancelPending = acc.cancelPending
                    + days.reduce((acc, day) => (
                        acc
                        + job.assignments[day].cancelledAssignments.filter(
                            (g) => !!g.assignedEquipment && !g.confirmedOn,
                        ).length
                    ), 0);
                const cancelConfirmed = acc.cancelConfirmed
                    + days.reduce((acc, day) => (
                        acc
                        + job.assignments[day].cancelledAssignments.filter(
                            (g) => !!g.assignedEquipment && !!g.confirmedOn,
                        ).length
                    ), 0);
                return {
                    cancelPending,
                    cancelConfirmed,
                };
            },
            { ...accumulator },
        );

        return {
            staff: {
                cancelPending: staff.cancelPending,
                cancelConfirmed: staff.cancelConfirmed,
            },
            equipment: {
                cancelPending: eq.cancelPending,
                cancelConfirmed: eq.cancelConfirmed,
            },
        };
    };

    getEventBoard = () => {
        const { planningJobs, cancellationJobs, selectedWeekRangeDays } = { ...this.state };
        const accumulator = {
            shiftStart: 0,
            jobStart: 0,
            jobEnd: 0,
            shiftEnd: 0,
            noShow: 0,
        };
        const days = selectedWeekRangeDays.map((wrd) => wrd.getDay());
        const allJobs = [...(planningJobs ?? []), ...(cancellationJobs ?? [])];

        return allJobs.reduce(
            (acc, job) => {
                const shiftStart = acc.shiftStart
                    + days.reduce((acc, day) => (
                        acc
                        + (job.assignments[day].staff ?? job.assignments[day].cancelledAssignments).filter(
                            (g) => g.lastEvent == EventStatus.ShiftStart,
                        ).length
                    ), 0);
                const jobStart = acc.jobStart
                    + days.reduce((acc, day) => (
                        acc
                        + (job.assignments[day].staff ?? job.assignments[day].cancelledAssignments).filter(
                            (g) => g.lastEvent == EventStatus.JobStart,
                        ).length
                    ), 0);
                const jobEnd = acc.jobEnd
                    + days.reduce((acc, day) => (
                        acc
                        + (job.assignments[day].staff ?? job.assignments[day].cancelledAssignments).filter(
                            (g) => g.lastEvent == EventStatus.JobEnd,
                        ).length
                    ), 0);
                const shiftEnd = acc.shiftEnd
                    + days.reduce((acc, day) => (
                        acc
                        + (job.assignments[day].staff ?? job.assignments[day].cancelledAssignments).filter(
                            (g) => g.lastEvent == EventStatus.ShiftEnd,
                        ).length
                    ), 0);
                const noShow = acc.noShow
                    + days.reduce((acc, day) => (
                        acc
                        + (job.assignments[day].staff ?? job.assignments[day].cancelledAssignments).filter(
                            (g) => g.lastEvent == EventStatus.NoShow,
                        ).length
                    ), 0);
                return {
                    shiftStart,
                    jobStart,
                    jobEnd,
                    shiftEnd,
                    noShow,
                };
            },
            { ...accumulator },
        );
    };

    loadCancellations = async () => {
        if (this.state.cancellationsLoading) {
            return null;
        }

        this.setState({ cancellationsLoading: true });

        const {
            cancellationJobSearchText,
            selectedWeek,
            selectedJobDispatchLocation,
            selectedWeekRangeDays,
        } = { ...this.state };
        const searchParams = new DispatchJobSearchParams();

        searchParams.weekStartDate = new Date(selectedWeek.start);
        searchParams.weekEndDate = new Date(selectedWeek.end);
        searchParams.searchText = cancellationJobSearchText ?? '';
        searchParams.dispatchLocation = selectedJobDispatchLocation;
        searchParams.selectedDays = selectedWeekRangeDays.map((x) => new Date(x).getDay())
            ?? [];

        const jobData = await httpClient.post(
            ApiRoutes.dispatch.cancelledJobs(),
            searchParams,
        );

        const { cancelJobs } = this.state;
        const updatedCancelJobs = [...jobData.rows];
        updatedCancelJobs.push(...cancelJobs);
        this.setState(
            {
                cancellationsLoading: false,
                cancellationJobs: jobData.rows,
                cancellationJobsFilteredCount: jobData.filterCount,
                cancellationJobsMaxRows: jobData.maxRows,
                crossDispatchCancellationJobs: updatedCancelJobs,
            },
        );

        return jobData.rows;
    };

    getCancellations = debounce(this.loadCancellations, 250);

    onCancellationTypeChanged = async () => {
        const rows = await this.loadCancellations();

        this.setState((prev) => {
            const current = prev.selectedWorkflowJob;
            if (!current || !rows) {
                return null;
            }

            const refreshed = rows.find((j) => j.id === current.job.id);
            if (!refreshed) {
                return null;
            }

            return {
                selectedWorkflowJob: {
                    job: refreshed,
                    day: current.day,
                    assignments: { ...(refreshed.assignments?.[current.day] ?? {}) },
                },
            };
        });
    };

    getCurrentResource = () => {
        const {
            activeResourceTab, staff, equipment,
        } = this.state;

        if (activeResourceTab.number === DispatchResourceTabNumbers.Staff) {
            const hidden = this.getHiddenStaffIds();

            const rows = staff.rows.filter((item) => !hidden.includes(item.id));

            return {
                rows,
                maxRows: staff.maxRows,
                filterCount: rows.length,
            };
        }

        const hidden = this.getHiddenEquipmentIds();

        const rows = equipment.rows.filter((item) => !hidden.includes(item.id));

        return {
            rows,
            maxRows: equipment.maxRows,
            filterCount: rows.length,
        };
    };

    getEmployeeDailyAttendanceEvents = async () => {
        const {
            employeeDailyAttendanceEventsLoading,
            selectedWeekRangeDays,
            selectedJobDispatchLocation,
            selectedResourceLocation,
            dispatchEmployeePermission,
            dispatchEquipmentPermission,
        } = this.state;

        const hasSelectedDays = selectedWeekRangeDays && selectedWeekRangeDays.length > 0;

        if (!employeeDailyAttendanceEventsLoading) {
            this.setState({
                employeeDailyAttendanceEventsLoading: true,
            });

            // Get tenant settings and permissions
            const { tenantSettings } = this.state;
            const { allowCrossDispatching } = tenantSettings || {};

            const useResourceLocation = allowCrossDispatching && (dispatchEquipmentPermission || dispatchEmployeePermission);
            const dispatchLocationId = useResourceLocation ? selectedResourceLocation : selectedJobDispatchLocation;

            let payload = null;
            if (hasSelectedDays && dispatchLocationId != null) {
                if (selectedWeekRangeDays.length === 1) {
                    payload = {
                        eventDate: moment(selectedWeekRangeDays[0]).toDate(),
                        dispatchLocationId,
                    };
                } else {
                    const sorted = [...selectedWeekRangeDays].sort((a, b) => moment(a).valueOf() - moment(b).valueOf());
                    payload = {
                        startDate: moment(sorted[0]).toDate(),
                        endDate: moment(sorted[sorted.length - 1]).toDate(),
                        dispatchLocationId,
                    };
                }
            }

            let employeeDailyAttendanceEvents = [];
            try {
                if (payload) {
                    employeeDailyAttendanceEvents = await httpClient.post(
                        ApiRoutes.employeeDailyAttendanceEvent.allFor(),
                        payload,
                    ) ?? [];
                }
            } catch (error) {
                console.error('Error fetching employee daily attendance events:', error);
                toasty.error('Unable to load daily attendance events.');
            } finally {
                this.setState(
                    {
                        employeeDailyAttendanceEvents,
                        employeeDailyAttendanceEventsLoading: false,
                    },
                );
            }
        }
    };

    getEmployeeLongTermStatuses = async (staff) => {
        const { employeeLongTermStatusesLoading } = this.state;

        if (!employeeLongTermStatusesLoading) {
            this.setState({ employeeLongTermStatusesLoading: true });

            const searchParams = this.getResourceSearchParams();

            const employeeLongTermStatuses = await httpClient.post(
                ApiRoutes.employeeLongTermStatus.allEffectiveBetween(),
                searchParams,
            );

            this.setState({
                employeeLongTermStatuses,
                employeeLongTermStatusesLoading: false,
            });

            return this.hideRestrictedStaff(staff, employeeLongTermStatuses);
        }
    };

    getEquipmentLongTermStatuses = async (equipment) => {
        const { equipmentLongTermStatusesLoading } = this.state;

        if (!equipmentLongTermStatusesLoading) {
            this.setState({ equipmentLongTermStatusesLoading: true });

            const searchParams = this.getResourceSearchParams();

            const equipmentLongTermStatuses = await httpClient.post(
                ApiRoutes.equipmentLongTermStatus.allEffectiveBetween(),
                searchParams,
            );

            this.setState({
                equipmentLongTermStatuses,
                equipmentLongTermStatusesLoading: false,
            });

            return this.hideRestrictedEquipment(equipment, equipmentLongTermStatuses);
        }
    };

    getEquipment = debounce(async () => {
        if (!this.state.equipmentLoading) {
            this.setState({ equipmentLoading: true });

            const searchParams = this.getResourceSearchParams();
            const equipment = await httpClient.post(
                ApiRoutes.dispatch.equipment(),
                searchParams,
            );

            const { equipmentLongTermStatuses } = this.state;

            this.setState({
                equipment,
                equipmentLoading: false,
            }, () => {
                this.hideRestrictedEquipment(equipment, equipmentLongTermStatuses);
            });
        }
    }, 250);

    getHiddenEquipmentIds = () => {
        const { equipment, activeResourceTab, filters } = this.state;

        if (!equipment?.rows) {
            return [];
        }

        const hiddenEquipmentIds = equipment.rows.filter((e) => {
            let visible = true;
            const filter = filters.find((f) => f.filterType === FilterTypes.EQUIPMENT_TYPE);

            if (filter) {
                visible = filter.equipIds.some((rid) =>
                    e.equipmentTypeId === rid,
                );
            }

            return !visible;
        }).map((e) => e.id);

        if (activeResourceTab.number === DispatchResourceTabNumbers.Equipment) {
            hiddenEquipmentIds.push(
                ...equipment.rows
                    .filter((eq) => this.equipmentShouldBeHidden(eq))
                    .map((eq) => eq.id),
            );
        }

        return hiddenEquipmentIds;
    };

    getHiddenStaffIds = () => {
        const {
            onlyShowAttendance,
            onlyShowUnassigned,
            onlyShowCancelled,
            staff,
            filters,
        } = this.state;

        if (!staff?.rows) {
            return [];
        }

        const now = new Date();

        const hiddenStaffIds = staff.rows.filter((s) => {
            let visible = true;
            const groupFilter = filters.find((f) => f.filterType === FilterTypes.GROUP);

            if (groupFilter) {
                visible = groupFilter.groupIds.some((rid) =>
                    s.groups.some((g) => g.id === rid),
                );
            }

            const certFilter = filters.find((f) => f.filterType === FilterTypes.CERTIFICATION);

            if (visible && certFilter) {
                visible = certFilter.certIds.some((certId) =>
                    s.employeeCertifications.some((ec) =>
                        ec.certificationId === certId
                        && ec.isActive
                        && new Date(ec.effectiveDate) <= now
                        && (new Date(ec.expirationDate) >= now || !ec.expirationDate),
                    ),
                );
            }

            return !visible;
        }).map((s) => s.id);

        if (onlyShowAttendance || onlyShowUnassigned || onlyShowCancelled) {
            hiddenStaffIds.push(
                ...staff.rows
                    .filter((s) => this.staffShouldBeHidden(s))
                    .map((s) => s.id),
            );
        }

        return hiddenStaffIds;
    };

    getJobs = async (providedJobs) => {
        if (!this.state.jobsLoading) {
            this.setState({ jobsLoading: true });

            const {
                allJobs,
                selectedWeek,
                jobLoadedWeek,
                selectedJobDispatchLocation,
                jobLoadedDispatchLocation,
            } = this.state;

            // If the selected week & dispatch location are what was loaded last,
            // use the list of allJobs.  Otherwise, get a fresh copy of the jobs.
            // We get all jobs first because we want awareness of all job assignments for all staff/equipment
            // even if that assignment falls on a day where a given job is not on the list of selected days.
            // Allows use to see all days during the week that staff/equipment are assigned.
            const allData = selectedWeek === jobLoadedWeek && selectedJobDispatchLocation === jobLoadedDispatchLocation
                ? providedJobs || allJobs
                : await this.getAllJobs();

            // planningJobs - jobs that fall within the selected days of the week.
            // We do filter the jobs display based on this.
            const pjs = allData.filter(this.isFilteredPlanningJob);

            this.setState({
                jobsLoading: false,
                jobLoadedWeek: selectedWeek,
                jobLoadedDispatchLocation: selectedJobDispatchLocation,
                allJobs: allData,
                planningJobs: pjs,
                planningJobsFilteredCount: pjs.length,
                planningJobsMaxRows: allData.length,
            });
        }
    };

    getResourceSearchParams = () => {
        const {
            resourceSearchText,
            selectedWeekRangeDays,
            selectedResourceLocation,
        } = { ...this.state };

        const searchParams = new DispatchResourceSearchParams();
        searchParams.dates = (selectedWeekRangeDays ?? []).map((d) => d) ?? [];
        searchParams.searchText = resourceSearchText ?? '';
        searchParams.dispatchLocation = selectedResourceLocation;
        searchParams.resourceLocation = selectedResourceLocation;
        return searchParams;
    };

    getResources = async (fetchJobs = false) => {
        const { selectedWeek, selectedJobDispatchLocation } = this.state;
        if (!this.state.resourcesLoading) {
            this.setState({
                resourcesLoading: true,
                crossJobsLoading: fetchJobs,
                hiddenEmployeeCount: 0,
            });

            await this.getEmployeeDailyAttendanceEvents();

            if (fetchJobs) {
                const searchParams = this.getResourceSearchParams();
                searchParams.dispatchLocation = searchParams.resourceLocation;

                const [staff, equipment] = await Promise.all([
                    httpClient.post(ApiRoutes.dispatch.staff(), searchParams),
                    httpClient.post(
                        ApiRoutes.dispatch.equipment(),
                        searchParams,
                    ),
                ]);

                let allStaffIds = [];

                if (staff && staff.rows) {
                    allStaffIds = staff.rows.map((s) => s.id);
                }

                let allEquipmentIds = [];
                if (equipment && equipment.rows) {
                    allEquipmentIds = equipment.rows.map((e) => e.id);
                }

                const [updatedStaff, updatedEquipment] = await Promise.all([
                    this.getEmployeeLongTermStatuses(staff),
                    this.getEquipmentLongTermStatuses(equipment),
                ]);

                // derive unique groups from staff.groups
                const uniqueGroups = uniqBy(
                    updatedStaff.rows.flatMap((s) => s.groups),
                    (g) => g.id,
                );
                // derive unique equipment types
                const uniqueEquipTypes = uniqBy(
                    updatedEquipment.rows.map((e) => e.equipmentType),
                    (t) => t.id,
                );

                this.setState({
                    allStaffIds,
                    resourcesLoading: false,
                    staff: updatedStaff,
                    equipment: updatedEquipment,
                    employeeGroups: uniqueGroups,
                    equipmentTypes: uniqueEquipTypes,
                }, async () => {
                    const crossSearchParams = new CrossDispatchJobSearchParams();
                    crossSearchParams.weekStartDate = new Date(selectedWeek.start);
                    crossSearchParams.weekEndDate = new Date(selectedWeek.end);
                    crossSearchParams.dispatchLocation = selectedJobDispatchLocation;
                    crossSearchParams.staffIDs = allStaffIds;
                    crossSearchParams.equipmentIDs = allEquipmentIds;
                    crossSearchParams.selectedDays = null;

                    const crossJobs = await httpClient.post(
                        ApiRoutes.dispatch.crossDispatchJobs(),
                        crossSearchParams,
                    );

                    const resourceJobs = crossJobs.jobPlanningViewModel ?? [];
                    const crossCancelJobs = crossJobs.jobCancellationViewModel ?? [];

                    // Merge cross-dispatch cancellations with regular cancellations
                    this.setState((prevState) => ({
                        resourceJobs,
                        cancelJobs: crossCancelJobs,
                        crossDispatchCancellationJobs: [...prevState.cancellationJobs, ...crossCancelJobs],
                        crossJobsLoading: false,
                    }));
                });
            } else {
                this.setState({
                    resourcesLoading: false,
                });
            }
        }
    };

    getStaff = debounce(async () => {
        if (!this.state.staffLoading) {
            this.setState({
                staffLoading: true,
                hiddenEmployeeCount: 0,
            });

            const searchParams = this.getResourceSearchParams();
            const staff = await httpClient.post(
                ApiRoutes.dispatch.staff(),
                searchParams,
            );

            const { employeeLongTermStatuses } = this.state;

            this.setState({ staff, staffLoading: false }, () => {
                this.hideRestrictedStaff(staff, employeeLongTermStatuses);
            });
        }
    }, 250);

    setCrewLead = async (jobAssignmentId, jobId) => {
        // Block the UI for the job while saving the crew lead.  There is also validation done here.
        await this.setJobSaving(jobId);
        await httpClient.js(ApiRoutes.dispatch.setCrewLead(jobAssignmentId));
        await this.setJobNotSaving(jobId);
    };

    setJobSaving = async (jobIds) => {
        const list = Array.isArray(jobIds) ? jobIds : [jobIds];
        if (!list.length || list.some((id) => id == null))
            throw new Error('DispatchBoard.setJobSaving: jobId is required');

        await this.setStateAsync((prev) => ({
            jobsSaving: [...prev.jobsSaving, ...list],
        }));
    };

    setJobNotSaving = async (jobIds) => {
        const list = Array.isArray(jobIds) ? jobIds : [jobIds];
        if (!list.length || list.some((id) => id == null))
            throw new Error('DispatchBoard.setJobNotSaving: jobId is required');

        await this.setStateAsync((prev) => ({
            jobsSaving: prev.jobsSaving.filter((id) => !list.includes(id)),
        }));
    };

    onReconnectHub = async (hub) => {
        try {
            await hub.invoke('JoinGroup', 'Dispatch');
        } catch (e) {
            console.info('Unable to join dispatch hub', e);
        }

        await Promise.all([
            this.getResources(true),
            this.getCancellations(),
        ]);

        const allData = await this.getAllJobs();
        const pjs = allData.filter(this.isFilteredPlanningJob);

        await this.setStateAsync((prev) => ({
            allJobs: allData,
            planningJobs: pjs,
            planningJobsFilteredCount: pjs.length,
            planningJobsMaxRows: allData.length,
            jobLoadedWeek: prev.selectedWeek,
            jobLoadedDispatchLocation: prev.selectedJobDispatchLocation,
        }));
    };

    canResourceBeAssigned = (draggableResourceType, ...args) => {
        if (draggableResourceType === DraggableResourceTypes.Staff) {
            return this.canStaffBeAssigned(...args);
        }

        return this.canEquipmentBeAssigned(...args);
    };

    isBulkAssignmentContext = () => this.state?.isCtrlPressed || this.state?.mobileIsBulkAssignment || false;

    // Equipment assignment validation
    canEquipmentBeAssigned = (
        sourceData,
        allAssignmentsForTheDayAndJob,
        targetAssignmentData,
        job,
        assignment,
        assignmentsForAllJobsForTheDay,
    ) => {
        const isValidItem = false;
        const warnings = [];

        const { longTermStatuses, equipmentLongTermStatuses } = this.state;

        const dayJobAssignmentExists = allAssignmentsForTheDayAndJob.find(
            (g) => !!g.assignment && g.assignment.id === sourceData.id && !sourceData.equipmentType.isGeneric,
        );

        const isBulk = this.isBulkAssignmentContext();

        if (dayJobAssignmentExists) {
            if (isBulk) {
                return {
                    isValidItem: false,
                    warnings,
                    errors: [],
                };
            }

            return {
                isValidItem,
                warnings,
                errors: [
                    `Assignment Invalid : ${sourceData.description} has already been scheduled for the selected time slot.`,
                ],
            };
        }
        // Addresses #1783
        // If not assigned to the target job, warn if the staff member is assigned to another job for the same day.
        const anyAssignmentExistsForDayAndJob = !!(
            assignmentsForAllJobsForTheDay.filter(
                (a) => !!a.assignment && a.assignment.id === sourceData.id && !sourceData.equipmentType.isGeneric,
            ) ?? []
        ).length;

        const assignmentDate = moment(assignment.assignmentDate);
        const targetJobId = job.id;
        const isAlreadyAssignedSomewhere = !sourceData.equipmentType.isGeneric && this.state.allJobs.some((currentJob) =>
            Object.entries(currentJob.assignments).some(([dayIndex, dayAssign]) => {
                // Calculate the actual date for this assignment based on the job's start date and day index
                const jobStartDate = moment(currentJob.startDate);
                const dayDate = jobStartDate.clone().startOf('week').add(parseInt(dayIndex), 'days');

                // Only check equipment assignments for the same date, excluding the target job
                return dayDate.isSame(assignmentDate, 'day') && currentJob.id !== targetJobId && dayAssign.equipment.some((eq) =>
                    eq.assignment?.id === sourceData.id,
                );
            }),
        );

        if (isAlreadyAssignedSomewhere || anyAssignmentExistsForDayAndJob) {
            warnings.push(`Equipment Assigned To Multiple Jobs: [${sourceData.equipmentType.description}] ${sourceData.identifier} is assigned to more than one job. Please review the assignments and ensure this is correct.`);
        }

        const longTermStatusRestrictionMessage = getEquipmentLongTermStatusRestrictionMessage(
            sourceData,
            assignment,
            longTermStatuses,
            equipmentLongTermStatuses,
            warnings,
        );

        if (longTermStatusRestrictionMessage) {
            if (isBulk) {
                return {
                    isValidItem: false,
                    warnings: [longTermStatusRestrictionMessage],
                    errors: [],
                };
            }

            return {
                isValidItem,
                warnings,
                errors: [
                    longTermStatusRestrictionMessage,
                ],
            };
        }

        // Other rules

        // const equipmentName = sourceData.name || sourceData.description;

        // Does the dragged item's equipment type match one of the required equipment types of the assignment?
        if (
            !targetAssignmentData.requiredEquipment
                .map((e) => e.id)
                .includes(sourceData.equipmentTypeId)
        ) {
            return {
                isValidItem,
                warnings,
                errors: [
                    `Assignment Invalid: ${sourceData.description} (${sourceData.equipmentType.description}) does not belong to equipment type: ${targetAssignmentData.requiredEquipment.map((e) => e.description).join(', ')}.`,
                ],
            };
        }

        return { isValidItem: true, errors: [], warnings };
    };

    // Staff assignment validation
    canStaffBeAssigned = (
        sourceData,
        allAssignmentsForTheDayAndJob,
        targetAssignmentData,
        job,
        assignment,
        assignmentsForAllJobsForTheDay,
    ) => {
        const {
            longTermStatuses,
            employeeLongTermStatuses,
            dailyAttendanceEvents,
            employeeDailyAttendanceEvents,
        } = this.state;

        const warnings = [];
        const isValidItem = false;

        const dayJobAssignmentExists = allAssignmentsForTheDayAndJob.find(
            (g) => !!g.assignment && g.assignment.id === sourceData.id,
        );

        const isBulk = this.isBulkAssignmentContext();

        if (dayJobAssignmentExists) {
            if (isBulk) {
                return {
                    isValidItem: false,
                    warnings,
                    errors: [],
                };
            }

            return {
                isValidItem,
                warnings,
                errors: [
                    `Assignment Invalid: ${sourceData.name} has already been scheduled for the selected time slot.`,
                ],
            };
        }
        // Addresses #1783
        // If not assigned to the target job, warn if the staff member is assigned to another job for the same day.
        const anyAssignmentExistsForDayAndJob = !!(
            assignmentsForAllJobsForTheDay.filter(
                (a) => !!a.assignment && a.assignment.id === sourceData.id,
            ) ?? []
        ).length;

        if (anyAssignmentExistsForDayAndJob) {
            const warning = `Employee Assigned To Multiple Jobs: ${sourceData.name} is assigned to more than one job. Please review the assignments and confirm this is correct.`;
            warnings.push(warning);
        }

        // Other rules

        // Does the dragged item's employee group match one of the required groups?
        if (
            !intersection(
                targetAssignmentData.assignableGroups.map((e) => e.id),
                sourceData.groups.map((g) => g.id),
            ).length
        ) {
            return {
                isValidItem,
                warnings,
                errors: [
                    `Assignment Invalid: ${sourceData.name} does not belong to groups: ${targetAssignmentData.assignableGroups.map((e) => e.groupName).join(', ')}`,
                ],
            };
        }

        const longTermStatusRestrictionMessage = getStaffLongTermStatusRestrictionMessage(
            sourceData,
            assignment,
            longTermStatuses,
            employeeLongTermStatuses,
            warnings,
        );

        if (longTermStatusRestrictionMessage) {
            if (isBulk) {
                return {
                    isValidItem: false,
                    warnings: [longTermStatusRestrictionMessage],
                    errors: [],
                };
            }

            return {
                isValidItem,
                warnings,
                errors: [
                    longTermStatusRestrictionMessage,
                ],
            };
        }

        const dailyAttendanceEventRestrictionMessage = getDailyAttendanceEventRestrictionMessage(
            sourceData,
            assignment,
            dailyAttendanceEvents,
            employeeDailyAttendanceEvents,
        );

        if (dailyAttendanceEventRestrictionMessage) {
            if (isBulk) {
                return {
                    isValidItem: false,
                    warnings: [dailyAttendanceEventRestrictionMessage],
                    errors: [],
                };
            }

            return {
                isValidItem,
                warnings,
                errors: [
                    dailyAttendanceEventRestrictionMessage,
                ],
            };
        }

        const employeeRestrictionMessage = getEmployeeRestrictionMessage(
            sourceData,
            job,
            assignment.assignmentDate,
        );

        if (employeeRestrictionMessage) {
            return {
                isValidItem,
                warnings,
                errors: [
                    employeeRestrictionMessage,
                ],
            };
        }

        const empToEmpMsg = getEmployeeToEmployeeRestrictionMessage(
            sourceData,
            allAssignmentsForTheDayAndJob,
            this.state.staff?.rows,
            assignment.assignmentDate,
        );

        if (empToEmpMsg) {
            return {
                isValidItem,
                warnings,
                errors: [
                    empToEmpMsg,
                ],
            };
        }

        const timeRestrictionMessage = getTimeBasedRestrictionMessage(
            sourceData,
            job,
            assignment.assignmentDate,
        );

        if (timeRestrictionMessage) {
            return {
                isValidItem,
                warnings,
                errors: [
                    timeRestrictionMessage,
                ],
            };
        }

        // todo: is this an assignable group
        if (!this.state.isCtrlPressed && !this.state.mobileIsBulkAssignment) {
            const certificationValidationMessage = getCertificationValidationMessage(
                sourceData,
                assignment.assignmentDate,
            );

            if (certificationValidationMessage) {
                return {
                    isValidItem,
                    warnings,
                    errors: [
                        certificationValidationMessage,
                    ],
                };
            }
        }

        return { isValidItem: true, errors: [], warnings };
    };



    // #endregion

    // #region JOBS

    // Accept job if it has staff/equipment assignments in one of the selected days of the week.
    doesJobPassWeek = (job) => {
        const { selectedWeekRangeDays } = this.state;

        // index 0: day of the week (0-7), 1: the assignment.
        const entries = Object.entries(job.assignments);
        for (const entry of entries) {
            const [_, a] = entry;

            if (a.staff.length > 0 || a.equipment.length > 0) {
                const compDay = parseInt(entry[0]);

                if (selectedWeekRangeDays.find((d) => d.getDay() === compDay)) return true;
            }
        }

        return false;
    };

    equipmentShouldBeHidden = (equipment) => {
        const {
            onlyShowUnassignedEquipment, allJobs, resourceJobs, selectedWeekRangeDays, equipmentLongTermStatuses,
        } = this.state;

        if (
            !onlyShowUnassignedEquipment
            || selectedWeekRangeDays.length !== 1
        ) {
            return false;
        }

        if (onlyShowUnassignedEquipment) {
            if (equipmentLongTermStatuses.find(
                (elts) => elts.equipmentId == equipment.id,
            )) {
                return false;
            }
        }

        const [selectedDate] = selectedWeekRangeDays;
        let hideEquipment = false;

        [...allJobs, ...(resourceJobs || [])].forEach((j) => Object.values(j.assignments).forEach((a) => {
            if (
                a.isCancelled
                || !moment(selectedDate).isSame(moment(a.date), 'day')
                || !a.equipment
            ) {
                return;
            }

            Object.values(a.equipment).forEach((equipmentOpening) => {
                if (
                    equipmentOpening.assignment
                    && equipmentOpening.assignment.id == equipment.id
                ) {
                    hideEquipment = true;
                }
            });
        }));

        return hideEquipment;
    };

    handleCancellationJobSearchText = async (value) => {
        this.setState({ cancellationJobSearchText: value });
        await this.getCancellations();
    };

    handleJobSearchText = (value) => {
        this.setState({ jobSearchText: value }, this.getJobs);
    };

    handleResourceSearchText = async (value) => {
        const { activeResourceTab } = { ...this.state };

        this.setState({ resourceSearchText: value });

        switch (activeResourceTab.number) {
            case DispatchResourceTabNumbers.Staff:
                await this.getStaff();
                break;
            case DispatchResourceTabNumbers.Equipment:
                await this.getEquipment();
                break;
            case DispatchResourceTabNumbers.Crews:
                return false; // not yet implemented
            default:
                break;
        }
    };

    hideStaffDisplayDueToLongTermStatus = (staff, employeeLongTermStatuses) => {
        const { longTermStatuses, selectedWeekRangeDays } = this.state;

        if (!staff) return true;
        if (!longTermStatuses?.length || !employeeLongTermStatuses?.length) return false;

        const selectedDays = (selectedWeekRangeDays ?? []).map((d) =>
            moment(d).startOf('day'),
        );

        if (!selectedDays.length) return false;

        const employeeStatuses = employeeLongTermStatuses.filter(
            (elts) => elts.employeeId == staff.id,
        );

        if (!employeeStatuses.length) return false;

        const hideStatuses = employeeStatuses.filter((elts) => {
            const meta = longTermStatuses.find(
                (lts) => lts.id === elts.longTermStatusId,
            );
            return meta?.hideFromDispatchBoard === true;
        });

        if (!hideStatuses.length) return false;

        const allSelectedDaysHidden = selectedDays.every((day) =>
            hideStatuses.some((status) => isDayWithinLongTermStatus(day, status)),
        );

        return allSelectedDaysHidden;
    };

    hideEquipmentDisplayDueToLongTermStatus = (equipment, equipmentLongTermStatuses) => {
        const { longTermStatuses, selectedWeekRangeDays } = this.state;

        if (!equipment) {
            return true;
        }

        if (!longTermStatuses?.length || !equipmentLongTermStatuses?.length) {
            return false;
        }

        const selectedDays = (selectedWeekRangeDays ?? []).map((d) =>
            moment(d).startOf('day'),
        );

        if (!selectedDays.length) return false;

        const equipmentStatuses = equipmentLongTermStatuses.filter(
            (elts) => elts.equipmentId == equipment.id,
        );

        if (!equipmentStatuses.length) return false;

        const hideStatuses = equipmentStatuses.filter((elts) => {
            const meta = longTermStatuses.find(
                (lts) => lts.id === elts.longTermStatusId,
            );
            return meta?.hideFromDispatchBoard === true;
        });

        if (!hideStatuses.length) return false;

        const allSelectedDaysHidden = selectedDays.every((day) =>
            hideStatuses.some((status) => isDayWithinLongTermStatus(day, status)),
        );

        return allSelectedDaysHidden;
    };

    hideRestrictedStaff = (staff, employeeLongTermStatuses) => {
        if (!staff || !staff.rows) {
            return;
        }

        const staffCount = staff.rows.length;

        staff.rows = staff.rows.filter(
            (s) => !this.hideStaffDisplayDueToLongTermStatus(s, employeeLongTermStatuses),
        );

        this.setState({ hiddenEmployeeCount: staffCount - staff.rows.length });

        return staff;
    };

    hideRestrictedEquipment = (equipment, equipmentLongTermStatuses) => {
        if (!equipment || !equipment.rows) {
            return equipment;
        }

        const equipmentCount = equipment.rows.length;

        equipment.rows = equipment.rows.filter(
            (s) => !this.hideEquipmentDisplayDueToLongTermStatus(s, equipmentLongTermStatuses),
        );

        this.setState({ hiddenEquipmentCount: equipmentCount - equipment.rows.length });

        return equipment;
    };

    isFilteredPlanningJob = (job) => {
        if (!job || !job.assignments) return false;

        const doesPass = this.doesJobPassWeek(job) && doesJobPassSearchText(job, this.state.jobSearchText);

        return doesPass;
    };

    populateState = async () => {
        const isAuthenticated = await authService.isAuthenticated();
        if (isAuthenticated) {
            const preferredDispatch = getPreferredDispatch();

            const {
                jobAssignmentCancellationTypes,
                jobCancellationTypes,
                jobCancellationReasons,
                longTermStatuses,
                dailyAttendanceEvents,
                certifications: certificationList,
                usCounties,
                ...vm
            } = await httpClient.js(ApiRoutes.dispatch.viewModel());

            const { tenantSettings } = this.context.tenant;

            const areAttendanceEventsEnabled = tenantSettings.attendanceEventsEnabled;

            if (!areAttendanceEventsEnabled) {
                dailyAttendanceEvents = [];
            }

            // var tomorrow = new Date();

            /// /RLC: Added to resolve issue with time rolling over to next day
            // tomorrow.setHours(0, 0, 0, 0);

            // tomorrow.setDate(tomorrow.getDate() + 1);

            let selectedWeek = { ...vm.weeks.find((x) => !!x.current) };

            const m_WeeksEnd = moment(selectedWeek.end);

            // pulling tomorrow from VM instead of using the above.
            const m_Tomorrow = moment(vm.tomorrow);

            // since we default them to tomorrow, we need to see if we must
            // flip to next week in the Weeks Dropdown.
            if (m_Tomorrow.isAfter(m_WeeksEnd, 'day')) {
                const nextWeek = vm.weeks.find((w) => moment(w.start).isSame(m_Tomorrow, 'day'));
                if (nextWeek) {
                    selectedWeek = { ...nextWeek };
                }
            }

            const searchParams = new URLSearchParams(this.props.location.search);

            const dispatchOfficeId = parseInt(searchParams.get('dispatchOfficeId'));
            const defaultDispatch = this.getDefaultDispatch(dispatchOfficeId, vm);

            const dateParam = searchParams.get('date');
            let selectedWeekRangeDays = [new Date(vm.tomorrow)];
            if (dateParam) {
                const parsedDate = moment(dateParam, 'YYYY-MM-DD');
                if (parsedDate.isValid()) {
                    const matchedWeek = vm.weeks.find((w) => parsedDate.isBetween(moment(w.start), moment(w.end), 'day', '[]'));
                    if (matchedWeek) {
                        selectedWeek = { ...matchedWeek };
                    }
                    selectedWeekRangeDays = [parsedDate.toDate()];
                }
            }

            const jobAssignmentCancellationTypeIdsThatPreventReassignment = getJobAssignmentCancellationTypeIdsThatPreventReassignment(
                jobAssignmentCancellationTypes,
            );

            this.setState(
                {
                    allJobs: [],
                    activeResourceTab: { ...DispatchResourceTabs[0] },
                    dispatchLocations: vm.dispatchLocations,
                    jobAssignmentCancellationTypes,
                    jobCancellationTypes,
                    jobCancellationReasons,
                    jobsSaving: [],
                    employeeGroups: vm.employeeGroups,
                    equipmentTypes: vm.equipmentTypes,
                    isAuthenticated,
                    isSavingJobStatus: false,
                    isCancellingJob: false,
                    isCancellingAssignment: false,
                    isCallingOutAssignment: false,
                    weeks: vm.weeks,
                    preferredDispatchLocation: preferredDispatch,
                    selectedResourceDispatchLocation: defaultDispatch,
                    selectedJobDispatchLocation: defaultDispatch,
                    selectedResourceLocation: defaultDispatch,
                    selectedWeekRangeDays,
                    selectedWeek,
                    longTermStatuses,
                    dailyAttendanceEvents,
                    areAttendanceEventsEnabled,
                    tenantSettings,
                    jobAssignmentCancellationTypeIdsThatPreventReassignment,
                    showResourceLocations: false,
                    certificationOptions: certificationList,
                    // Permissions
                    dispatchEquipmentPermission: this.context.checkUserHasPermission(ApplicationPermissions.dispatch_equipment),
                    dispatchEmployeePermission: this.context.checkUserHasPermission(ApplicationPermissions.dispatch_employee),
                    dispatchEditPermission: this.context.checkUserHasPermission(ApplicationPermissions.dispatch_edit),
                    equipmentAuditLogPermission: this.context.checkUserHasPermission(ApplicationPermissions.timesheet_equipment_audit_logs_view),
                    crossDispatchPermission: this.context.checkUserHasPermission(ApplicationPermissions.dispatch_employee),
                    usCounties,
                },
                async () => {
                    await Promise.all([
                        this.getResources(true),
                        this.getJobs(),
                        this.getCancellations(),
                    ]);

                    this.setState({ loading: false });
                },
            );
        }
    };

    getDefaultDispatch = (dispatchOfficeId, vm) => {
        const { user } = this.context;
        const firstDispatchResult = vm?.dispatchLocations?.[0]?.value ?? null;
        const preferredDispatch = getPreferredDispatch();

        if (dispatchOfficeId) {
            return dispatchOfficeId;
        }

        const isAdmin = user?.isAdmin ?? false;
        if (isAdmin && preferredDispatch) {
            return preferredDispatch;
        } else if (isAdmin) {
            return firstDispatchResult;
        }

        const dispatchCompanyId = user?.dispatchCompanyId;
        if (dispatchCompanyId) {
            const dispatchLocation = vm?.dispatchLocations?.find((dl) => dl.value === dispatchCompanyId);
            if (dispatchLocation) {
                const { companyTenantId } = dispatchLocation;
                if (companyTenantId === user.tenantId) {
                    return firstDispatchResult;
                }
                return dispatchCompanyId;
            }
        }

        return firstDispatchResult;
    };

    preferredDispatch = (id) => {
        if (!id) {
            localStorage.setItem('preferredDispatchLocation', '');
            this.setState({ preferredDispatchLocation: null });
        } else {
            localStorage.setItem('preferredDispatchLocation', id.toString());
            this.setState({ preferredDispatchLocation: id });
        }
    };

    saveAssignment = async (itemOrArray) => {
        const items = Array.isArray(itemOrArray) ? itemOrArray : [itemOrArray];
        if (!items.length) return null;

        const [{ weekOfDate }] = items;
        // Each item already names its host (employee or truck), so the server keeps those links as sent
        const payload = { weekOfDate, assignments: items, keepEquipmentLinks: true };

        try {
            return await httpClient.andGetResponse(
                httpClient.types.post,
                ApiRoutes.dispatch.saveAssignment(),
                payload,
                'Server Error Saving Job Assignment',
                null,
                true,
            );
        } finally {
            await this.getEmployeeDailyAttendanceEvents();
        }
    };

    selectedJobAssignmentCancellationTypePreventsReassignment = () => {
        const {
            selectedJobAssignmentCancellationType,
            jobAssignmentCancellationTypes,
        } = this.state;

        if (!selectedJobAssignmentCancellationType) {
            return false;
        }

        const jobAssignmentCancellationType = jobAssignmentCancellationTypes.find(
            (jact) => jact.id == selectedJobAssignmentCancellationType,
        );

        return Boolean(
            jobAssignmentCancellationType
            && jobAssignmentCancellationType.preventsReassignment,
        );
    };

    staffShouldBeHidden = (staff) => {
        const {
            onlyShowAttendance,
            onlyShowUnassigned,
            onlyShowCancelled,
            employeeDailyAttendanceEvents,
            employeeLongTermStatuses,
            allJobs,
            resourceJobs,
            selectedWeekRangeDays,
            cancellationJobs,
        } = this.state;

        const [selectedDate] = selectedWeekRangeDays;

        if (
            (!onlyShowAttendance
                && !onlyShowUnassigned
                && !onlyShowCancelled)
            || selectedWeekRangeDays.length !== 1
        ) {
            return false;
        }

        if (onlyShowAttendance) {
            return !employeeDailyAttendanceEvents.find(
                (eda) => eda.employeeId == staff.id,
            );
        }

        if (onlyShowCancelled) {
            let foundMatch = false;

            cancellationJobs.forEach((j) =>
                Object.values(j.assignments).forEach((a) => {
                    Object.values(a.cancelledAssignments).forEach((c) => {
                        if (!foundMatch) {
                            foundMatch =
                                c.assignedEmployeeId == staff.id &&
                                this.treatCancelAsCancelled(c) &&
                                moment(c.assignmentDate).isSame(
                                    selectedDate,
                                    'day',
                                );
                        }
                    });
                }),
            );

            return !foundMatch;
        }

        if (onlyShowUnassigned) {
            if (
                employeeDailyAttendanceEvents.find(
                    (eda) => eda.employeeId == staff.id,
                )
                || employeeLongTermStatuses.find(
                    (elts) => elts.employeeId == staff.id
                        && isDayWithinLongTermStatus(selectedDate, elts),
                )
            ) {
                return true;
            }

            let hideEmployee = false;

            [...allJobs, ...(resourceJobs || [])].forEach((j) => Object.values(j.assignments).forEach((a) => {
                if (
                    a.isCancelled
                    || !moment(selectedDate).isSame(moment(a.date), 'day')
                    || !a.staff
                ) {
                    return;
                }

                Object.values(a.staff).forEach((staffOpening) => {
                    if (
                        staffOpening.assignment
                        && staffOpening.assignment.id == staff.id
                    ) {
                        hideEmployee = true;
                    }
                });
            }));

            // return hideEmployee;

            if (hideEmployee) return true;

            // If employee still showing, check for cancellations
            let foundCancel = false;

            cancellationJobs.forEach((j) => Object.values(j.assignments).forEach((a) => {
                Object.values(a.cancelledAssignments).forEach((c) => {
                    if (!foundCancel) {
                        foundCancel = c.assignedEmployeeId == staff.id
                            && this.treatCancelAsCancelled(c)
                            && moment(c.assignmentDate).isSame(
                                selectedDate,
                                'day',
                            );
                    }
                });
            }));

            return foundCancel;
        }

        return false;
    };

    toggleCancelAssignmentModal = () => {
        const { showCancelAssignmentModal } = this.state;
        this.setState({
            showCancelAssignmentModal: !showCancelAssignmentModal,
        });
    };

    toggleJobCollapse = (jobId) => (collapsed) => {
        this.setState((prevState) => ({
            jobsOpened: collapsed ? prevState.jobsOpened.concat(jobId) : prevState.jobsOpened.filter((x) => x !== jobId),
        }));
    };

    jobIsOpened = (jobId) => this.state.jobsOpened.includes(jobId);

    toggleModal = () => {
        Prompt.show({
            title: 'Send Notifications',
            onSubmit: (values) => this.onSendNotifications(values),
            SaveButtonProps: {
                label: 'Send',
                savingLabel: 'Sending Notifications and Updating Jobs',
                icon: faBolt,
            },
            body: () => (
                <FormCheckbox
                    className="ml-1 mt-2"
                    id="noResponse"
                    name="noResponse"
                    labelText="Include employees with no response"
                />
            ),
        });
    };

    treatCancelAsCancelled = (employeeAssignment) => {
        const { jobAssignmentCancellationTypes } = this.state;

        return isCancelAssignmentTreatedAsCancelled(
            employeeAssignment,
            jobAssignmentCancellationTypes,
        );
    };

    updateEmployeeDailyAttendanceEvent = async (
        employeeId,
        dailyAttendanceEventId,
    ) => {
        const { selectedWeekRangeDays } = this.state;

        const eventDate = moment(selectedWeekRangeDays[0]).toDate();

        const postData = {
            employeeId,
            dailyAttendanceEventId,
            eventDate,
        };

        if (dailyAttendanceEventId == 0) {
            await httpClient.andGetResponse(
                httpClient.types.post,
                ApiRoutes.employeeDailyAttendanceEvent.delete(),
                postData,
                'Error Removing Daily Attendance Event',
                null,
                true,
            );
        } else {
            await httpClient.andGetResponse(
                httpClient.types.post,
                ApiRoutes.employeeDailyAttendanceEvent.upsert(),
                postData,
                'Error Updating Daily Attendance Event',
                null,
                true,
            );
        }

        await this.getEmployeeDailyAttendanceEvents();
    };

    handleAttendanceEventClick = (eventId) => {
        const { selectedAttendanceEvent } = this.state;

        if (selectedAttendanceEvent === eventId) {
            this.setState({
                footerIsVisible: false,
                selectedAttendanceEvent: null,
                selectedStaffForAssignment: [],
            });
            return;
        }

        this.setState({
            selectedAttendanceEvent: eventId,
            footerIsVisible: true,
        });
    };

    handleStaffCardClick = (staffId) => {
        const {
            selectedAttendanceEvent,
            selectedStaffForAssignment,
            employeeDailyAttendanceEvents,
            selectedWeekRangeDays,
        } = this.state;

        if (selectedAttendanceEvent === null) return;

        if (selectedAttendanceEvent === 0) {
            const hasAttendanceEvent = employeeDailyAttendanceEvents.find(
                (edae) => edae.employeeId === staffId
                    && moment(selectedWeekRangeDays[0]).isSame(edae.eventDate, 'day'),
            );

            if (!hasAttendanceEvent) {
                const card = document.getElementById(`staffCard-${staffId}`);
                if (card) {
                    card.classList.add('assignment-error');
                    setTimeout(() => {
                        card.classList.remove('assignment-error');
                    }, 1000);
                }
                return;
            }
        }

        const isSelected = selectedStaffForAssignment.includes(staffId);

        this.setState({
            selectedStaffForAssignment: isSelected
                ? selectedStaffForAssignment.filter((id) => id !== staffId)
                : [...selectedStaffForAssignment, staffId],
        });
    };

    handleStaffCardDoubleClick = async (staffId) => {
        const {
            selectedAttendanceEvent,
            employeeDailyAttendanceEvents,
            selectedWeekRangeDays,
        } = this.state;

        if (selectedAttendanceEvent === null) return;

        if (selectedAttendanceEvent === 0) {
            const hasAttendanceEvent = employeeDailyAttendanceEvents.find(
                (edae) => edae.employeeId === staffId
                    && moment(selectedWeekRangeDays[0]).isSame(edae.eventDate, 'day'),
            );

            if (!hasAttendanceEvent) return;
        }

        await this.assignStaffToAttendanceEvent([staffId]);
    };

    handleAssignmentCancel = () => {
        this.setState({
            selectedAttendanceEvent: null,
            selectedStaffForAssignment: [],
        });
    };

    handleAssignmentConfirm = async () => {
        const { selectedStaffForAssignment } = this.state;

        if (selectedStaffForAssignment.length === 0) return;

        this.setState({ isAssigningAttendance: true });

        try {
            await this.assignStaffToAttendanceEvent(selectedStaffForAssignment);
        } finally {
            this.setState({ isAssigningAttendance: false });
        }
    };

    assignStaffToAttendanceEvent = async (staffIds) => {
        const { selectedAttendanceEvent, selectedWeekRangeDays } = this.state;
        const eventDate = moment(selectedWeekRangeDays[0]).toDate();

        const postData = {
            employeeIds: staffIds,
            dailyAttendanceEventId: selectedAttendanceEvent,
            eventDate,
        };

        try {
            if (selectedAttendanceEvent === 0) {
                await httpClient.andGetResponse(
                    httpClient.types.post,
                    ApiRoutes.employeeDailyAttendanceEvent.delete(),
                    postData,
                    'Error Clearing Daily Attendance Events',
                    null,
                    true,
                );
            } else {
                await httpClient.andGetResponse(
                    httpClient.types.post,
                    ApiRoutes.employeeDailyAttendanceEvent.upsert(),
                    postData,
                    'Error Assigning Staff to Daily Attendance Event',
                    null,
                    true,
                );
            }

            if (selectedAttendanceEvent === 0) {
                toasty.success('Attendance assignments cleared!');
            } else {
                toasty.success('Staff successfully assigned!');
            }

            await this.getEmployeeDailyAttendanceEvents();

            this.setState({
                selectedAttendanceEvent: null,
                selectedStaffForAssignment: [],
            });
        } catch {
            toasty.error('Error assigning staff to daily attendance event');
        }
    };

    // #endregion

    // validation logic for drop zones
    isDropZoneValid = (dropZoneType, dropZoneAssignableGroupsOrDescription) => {
        const draggedResource = this.getDraggedResource();

        if (!draggedResource) return false;

        if (dropZoneType === DraggableResourceTypes.Equipment && draggedResource.equipmentType) {
            return draggedResource.equipmentType.description === dropZoneAssignableGroupsOrDescription;
        }

        if (dropZoneType === DraggableResourceTypes.Staff && draggedResource.groups) {
            return dropZoneAssignableGroupsOrDescription.some((group) =>
                draggedResource.groups.some((draggedGroup) => draggedGroup.id === group.id),
            );
        }

        return false;
    };

    // Filter modal handlers
    toggleFilterModal = () => {
        const {
            showFilterModal,
            filters,
            modalFilterGroups,
            modalFilterEquip,
            modalFilterCerts,
        } = this.state;

        this.clearSlotFilter();
        const isOpening = !showFilterModal;

        this.setState({
            showFilterModal: isOpening,
            modalFilterGroups: isOpening
                ? filters.find((f) => f.filterType === FilterTypes.GROUP)?.groupIds || []
                : modalFilterGroups,
            modalFilterEquip: isOpening
                ? filters.find((f) => f.filterType === FilterTypes.EQUIPMENT_TYPE)?.equipIds || []
                : modalFilterEquip,
            modalFilterCerts: isOpening
                ? filters.find((f) => f.filterType === FilterTypes.CERTIFICATION)?.certIds || []
                : modalFilterCerts,
        });
    };

    handleFilterModalSaveButtonClick = () => {
        const { modalFilterGroups = [], modalFilterEquip = [], modalFilterCerts = [] } = this.state;

        let newFilters = [];

        if (modalFilterGroups.length > 0) {
            newFilters = [
                ...newFilters,
                {
                    filterType: FilterTypes.GROUP,
                    groupIds: [...modalFilterGroups],
                },
            ];
        }

        if (modalFilterEquip.length > 0) {
            newFilters = [
                ...newFilters,
                {
                    filterType: FilterTypes.EQUIPMENT_TYPE,
                    equipIds: [...modalFilterEquip],
                },
            ];
        }

        if (modalFilterCerts.length > 0) {
            newFilters.push({
                filterType: FilterTypes.CERTIFICATION,
                certIds: [...modalFilterCerts],
            });
        }
        this.setState({
            showFilterModal: false,
            filters: newFilters,
            pulsingSlotId: null,
        });
    };

    handleCertificationButtonClick = (certificationId) => {
        let { filters } = this.state;
        const activeResourceTab = DispatchResourceTabs.find((t) => t.name === 'Staff');

        const existingFilter = filters.find((f) => f.filterType === FilterTypes.CERTIFICATION);

        if (existingFilter) {
            filters = filters.map((f) => {
                if (f.filterType === FilterTypes.CERTIFICATION) {
                    if (f.certIds.includes(certificationId)) {
                        const certIds = f.certIds.filter((cId) => cId !== certificationId);

                        if (certIds.length > 0) {
                            return {
                                ...f,
                                certIds,
                            };
                        }

                        return null;
                    }

                    return {
                        ...f,
                        certIds: f.certIds.concat(certificationId),
                    };
                }

                return f;
            }).filter((x) => x);
        } else {
            filters = filters.concat({
                filterType: FilterTypes.CERTIFICATION,
                certIds: [certificationId],
            });
        }

        this.setState({
            activeResourceTab,
            filters,
        });
    };

    handleFilterModalResetButtonClick = () => {
        this.setState({ modalFilterGroups: [], modalFilterEquip: [], modalFilterCerts: [] });
    };

    resetSavedResourceFilters() {
        this.setState({ filters: [], pulsingSlotId: null });
    }

    removeFilter = (filterType) => {
        const { filters } = this.state;
        this.clearSlotFilter();
        this.setState({
            filters: filters.filter((f) => f.filterType !== filterType),
        });
    };

    handleSlotSelect = (type, ids, uniqueId) => {
        const ft = type === DraggableResourceTypes.Staff
            ? FilterTypes.GROUP
            : FilterTypes.EQUIPMENT_TYPE;

        // pick the right tab
        const newTab = DispatchResourceTabs.find((t) =>
            ft === FilterTypes.GROUP ? t.name === 'Staff' : t.name === 'Equipment',
        );

        // Are we switching tabs?
        const isSwitchingTab = newTab !== this.state.activeResourceTab;

        // Base filters: either nothing (switching tabs) or remove this type only
        const baseFilters = isSwitchingTab
            ? []
            : this.state.filters.filter((f) => f.filterType !== ft);

        // If clicking the same slot again (and not switching tabs) → clear it
        if (!isSwitchingTab && this.state.pulsingSlotId === uniqueId) {
            return this.setState({
                filters: baseFilters,
                pulsingSlotId: null,
            });
        }

        // otherwise add a brand‐new filter
        const newFilter = {
            filterType: ft,
            id: uniqueId,
            ...(ft === FilterTypes.GROUP ? { groupIds: ids } : {}),
            ...(ft === FilterTypes.EQUIPMENT_TYPE ? { equipIds: ids } : {}),
        };



        this.setState({
            filters: [...baseFilters, newFilter],
            pulsingSlotId: uniqueId,
            activeResourceTab: newTab,
        });
    };

    clearSlotFilter() {
        const { filters, pulsingSlotId } = this.state;

        this.setState({
            filters: filters.filter((f) => f.id !== pulsingSlotId),
            pulsingSlotId: null,
        });
    }

    onSelectedWeekRangeDayClicked = (e, dayDate) => {
        const useRadio = !!this.state.tenantSettings?.useRadioStyleDaySelection;

        const isMetaKey = e.metaKey || e.ctrlKey || !useRadio;
        const isShiftKey = e.shiftKey;

        const current = this.state.selectedWeekRangeDays || [];
        let next = [];

        if (isMetaKey) {
            const exists = current.some((d) => moment(d).isSame(dayDate, 'day'));
            if (exists && current.length > 1) {
                next = current.filter((d) => !moment(d).isSame(dayDate, 'day'));
            } else if (!exists) {
                next = [...current, dayDate];
            } else {
                next = current;
            }
            next.sort((a, b) => moment(a).valueOf() - moment(b).valueOf());
        } else if (isShiftKey && current.length > 0) {
            const lastSelected = current[current.length - 1];
            const weekDays = getDaysInRange(
                moment(this.state.selectedWeek.start).toDate(),
                moment(this.state.selectedWeek.end).toDate(),
            );

            const startIdx = weekDays.findIndex((d) => moment(d).isSame(lastSelected, 'day'));
            const endIdx = weekDays.findIndex((d) => moment(d).isSame(dayDate, 'day'));

            if (startIdx !== -1 && endIdx !== -1) {
                const [start, end] = startIdx <= endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
                next = weekDays.slice(start, end + 1);
            }
        } else {
            next = [dayDate];
        }

        this.setState({
            selectedWeekRangeDays: next,
            selectedAttendanceEvent: null,
            selectedStaffForAssignment: [],
        }, () => {
            Promise.all([
                this.getJobs(),
                // this.getCancellations(),
                this.getResources(),
            ]);
        });
    };

    handlePlanningStatusToggle = (status) => {
        this.setState(({ selectedPlanningStatus }) => ({
            selectedPlanningStatus: selectedPlanningStatus === status ? null : status,
        }));
    };

    getFilteredPlanningJobs() {
        const {
            planningJobs,
            selectedWeekRangeDays,
            selectedPlanningStatus,
        } = this.state;

        if (!selectedPlanningStatus) return planningJobs;
        const days = selectedWeekRangeDays.map((d) => d.getDay());
        return planningJobs.filter((job) =>
            days.some((dayIdx) => doesJobPassPlanningFilter(job, dayIdx, selectedPlanningStatus)),
        );
    }

    onRemoveDayJob = (job, day) => {
        this.setState({
            showCancelJobModal: true,
            selectedDayJobToRemove: { day, job },
        });
    };

    onRemoveAssignment = (job, jobAssignment, day) => {
        this.setState({
            selectedJobAssignmentToRemove: { job, day, ...jobAssignment },
            showCancelAssignmentModal: true,
        });
    };

    handleCancellationStatusToggle = (status) => {
        this.setState(({ selectedCancellationStatus }) => ({
            selectedCancellationStatus: selectedCancellationStatus === status ? null : status,
        }));
    };

    getFilteredCancellationJobs() {
        const { cancellationJobs, selectedWeekRangeDays, selectedCancellationStatus } = this.state;

        return cancellationJobs.filter((job) =>
            selectedWeekRangeDays.some((selectedDay) => {
                const assignment = Object.values(job.assignments ?? {}).find((asn) =>
                    moment(asn.date).isSame(moment(selectedDay), 'day'),
                );

                return assignment?.cancelledAssignments?.some((canc) => {
                    const matchesDate = moment(canc.assignmentDate).isSame(moment(selectedDay), 'day');

                    if (!selectedCancellationStatus) {
                        return matchesDate;
                    }

                    const matchesStatus = selectedCancellationStatus === JobStatusTypes.CANCEL_PENDING
                        ? !canc.confirmedOn
                        : !!canc.confirmedOn;
                    return matchesDate && matchesStatus;
                });
            }),
        );
    }

    handleEventStatusToggle = (status) => {
        this.setState(({ selectedEventStatus }) => ({
            selectedEventStatus: selectedEventStatus === status ? null : status,
        }));
    };

    getFilteredEvents() {
        const {
            planningJobs,
            cancellationJobs,
            selectedWeekRangeDays,
            selectedEventStatus,
        } = this.state;

        const days = selectedWeekRangeDays.map((d) => d.getDay());
        const allJobs = [...(planningJobs ?? []), ...(cancellationJobs ?? [])];

        if (!selectedEventStatus) {
            return allJobs?.filter((job) =>
                days.some((dayIdx) => (job.assignments[dayIdx].staff ?? job.assignments[dayIdx].cancelledAssignments)?.some((staff) => !!staff?.assignment || !!staff?.assignedEmployeeId)),
            );
        }

        return allJobs?.filter((job) =>
            days.some((dayIdx) => (job.assignments[dayIdx].staff ?? job.assignments[dayIdx].cancelledAssignments)?.some((staff) => staff?.lastEvent === selectedEventStatus)),
        );
    }

    getHiddenResourceText = () => {
        const { activeResourceTab, hiddenEmployeeCount, hiddenEquipmentCount } = this.state;
        let hiddenResourceText = '';

        if (activeResourceTab.number === DispatchResourceTabNumbers.Staff) {
            hiddenResourceText = hiddenEmployeeCount > 0
                ? ` ${pluralize(hiddenEmployeeCount, 'employee')} hidden due to Long-Term Status.`
                : '';
        } else if (activeResourceTab.number === DispatchResourceTabNumbers.Equipment) {
            hiddenResourceText = hiddenEquipmentCount > 0
                ? ` ${pluralize(hiddenEquipmentCount, 'item')} hidden due to Long-Term Status.`
                : '';
        }

        return hiddenResourceText;
    };

    isCardDisabled = (employeeCertifications) => {
        const { certificationOptions } = this.state;

        /*const lastSelectedDay = selectedWeekRangeDays.reduce(
            (max, d) => (moment(d).isAfter(max) ? moment(d) : max),
            moment(selectedWeekRangeDays[0])
        );*/

        return certificationOptions.some((cert) => {
            if (!cert.mustBeValidForDispatch) return false;

            const match = employeeCertifications.find(
                (empCert) => empCert.certificationId === cert.id,
            );
            if (!match) return true;

            /*if (cert.willExpire && match.expirationDate) {
                if (moment(match.expirationDate).isBefore(lastSelectedDay, 'day')) return true;
            }*/
            return false;
        });
    };


    render() {
        const {
            activeJobTab,
            activeResourceTab,
            allJobsCollapsed,
            allCancellationJobsCollapsed,
            //crews,
            filters,
            crewsInterfaceOpen,
            dispatchLocations,
            jobAssignmentCancellationTypes,
            jobCancellationTypes,
            jobSearchText,
            cancellationJobSearchText,
            loading,
            dragging,
            equipment,
            isAuthenticated,
            jobsSaving,
            allJobs,
            resourceJobs,
            planningJobs,
            planningJobsFilteredCount,
            planningJobsMaxRows,
            crossDispatchCancellationJobs,
            cancellationJobs,
            cancellationJobsFilteredCount,
            cancellationJobsMaxRows,
            resourceSearchText,
            selectedWorkflowJob,
            selectedJobDispatchLocation,
            selectedResourceDispatchLocation,
            selectedWeek,
            selectedWeekRangeDays,
            staff,
            weeks,
            showCancelAssignmentModal,
            showCancelJobModal,
            longTermStatuses,
            employeeLongTermStatuses,
            equipmentLongTermStatuses,
            dailyAttendanceEvents,
            staffLoading,
            areAttendanceEventsEnabled,
            employeeDailyAttendanceEvents,
            onlyShowAttendance,
            onlyShowUnassigned,
            onlyShowCancelled,
            tenantSettings,
            equipmentLoading,
            onlyShowUnassignedEquipment,
            resourcesLoading,
            crossJobsLoading,
            jobAssignmentCancellationTypeIdsThatPreventReassignment,
            selectedResourceLocation,
            showFilterModal,
            modalFilterGroups,
            modalFilterEquip,
            modalFilterCerts,
            employeeGroups,
            equipmentTypes,
            pulsingSlotId,
            certificationOptions,
            selectedPlanningStatus,
            selectedCancellationStatus,
            selectedEventStatus,
            jobCancellationReasons,
            dispatchEditPermission,
            dispatchEmployeePermission,
            dispatchEquipmentPermission,
            equipmentAuditLogPermission,
            crossDispatchPermission,

            // Mobile assignment modal state
            showMobileAssignmentModal,
            mobileAssignmentResourceType,
            mobileAssignmentSlot,
            mobileAssignmentJob,
            mobileSelectedResource,
            mobileIsBulkAssignment,
            mobileSelectedStaffForEquipment,
        } = { ...this.state };
        const filteredJobs = this.getFilteredPlanningJobs();
        const searchParams = new URLSearchParams(this.props.location.search);
        const quickJobCreatedJobId = searchParams.get('jobId');

        // Group jobs by parentJobId for display
        const filteredCancellationJobs = this.getFilteredCancellationJobs();

        const filteredevents = this.getFilteredEvents();

        const staffTabActive = activeResourceTab.number === DispatchResourceTabNumbers.Staff;
        const equipmentTabActive = activeResourceTab.number === DispatchResourceTabNumbers.Equipment;

        const hiddenStaffIds = this.getHiddenStaffIds();
        const hiddenEquipmentIds = this.getHiddenEquipmentIds();

        // #region COMPUTED
        if (
            !isAuthenticated
            || !(this.context.tenant ?? {}).tenantSettings
            || loading
            || !selectedJobDispatchLocation
            || !selectedResourceDispatchLocation
        ) return <Progress className="flex-fill" />;

        const { crewsEnabled, allowCrossDispatching } = tenantSettings;

        const timesheetCheckinsEnabled = Boolean(
            tenantSettings.enableContractTimesheetCheckins,
        );

        const daysInRange = !selectedWeek
            ? null
            : getDaysInRange(
                moment(selectedWeek.start).toDate(),
                moment(selectedWeek.end).toDate(),
            );

        const currentResource = this.getCurrentResource();
        const planningBoard = getPlanningBoard(this.state.planningJobs, this.state.selectedWeekRangeDays);
        const cancellationBoard = this.getCancellationBoard();
        const eventBoard = this.getEventBoard();

        const oneDaySelected = (selectedWeekRangeDays ?? []).length === 1;
        const todaySelectedOrLater = !!oneDaySelected
            && !!moment(selectedWeekRangeDays[0]).isSameOrAfter(
                moment(new Date()),
                'day',
            );
        const jobDispatchLocation = (dispatchLocations ?? []).find(
            (x) => x.value === selectedJobDispatchLocation,
        ) ?? '';

        const resourceLocation = (dispatchLocations ?? []).find(
            (x) => x.value === selectedResourceLocation,
        ) ?? '';

        const showAttendance = !(
            loading
            || staffLoading
            || resourcesLoading
            || !staffTabActive
            || !areAttendanceEventsEnabled
            || !dailyAttendanceEvents
            || !dailyAttendanceEvents.length
            || !selectedWeekRangeDays
            || selectedWeekRangeDays.length != 1
        );
        // #endregion

        return (
            <>
                <DispatchHub
                    onJobCancellationUpdated={this.onJobCancellationUpdatedFromHub}
                    onJobCreated={this.onJobCreatedFromHub}
                    onJobUpdated={this.onJobUpdatedFromHub}
                    onSendMultipleOperations={this.onUpdateMultipleJobs}
                    onJobsUpdatedBulk={this.onJobsUpdatedBulkFromHub}
                    onJobDeleted={this.onJobDeletedFromHub}
                    onStaffRelatedDataUpdated={this.onStaffRelatedDataUpdatedFromHub}
                    onReconnect={this.onReconnectHub}
                />

                <div className="dispatch-board">
                    <DragDropContext
                        onDragStart={dispatchEditPermission ? this.onDragStart : undefined}
                        onDragEnd={dispatchEditPermission ? this.onDragEnd : undefined}
                        onBeforeCapture={dispatchEditPermission ? this.onBeforeDragCapture : undefined}
                    >
                        <div
                            id="dispatchWrapper"
                            className={cls({
                                'crews-open': crewsInterfaceOpen,
                            })}
                        >
                            <div className="dispatchPaneContainer">
                                <Split
                                    id="dispatchPane"
                                    className={cls('dispatchPane', !showAttendance && 'dispatchPaneFullHeight')}
                                    sizes={[60, 40]}
                                    minSize={[324, 300]}
                                    expandToMin={false}
                                    snapOffset={30}
                                    dragInterval={1}
                                    direction="horizontal"
                                    cursor="col-resize"
                                    elementStyle={SplitHelper.flex.elementStyle}
                                    gutter={() => {
                                        const div = document.createElement('div');
                                        div.className = 'vr gutter gutter-horizontal';

                                        return div;
                                    }}
                                >
                                    <Hide mdUp>
                                        <div
                                            id="dispatchLeft"
                                            className={cls({
                                                'pointer-events-none':
                                                    !!this.state.resourcesLoading,
                                                highlight: selectedJobDispatchLocation != selectedResourceLocation,
                                            })}
                                        >
                                            <div className="toolbar d-flex flex-row justify-content-between align-items-center">
                                                <div className="resource-picker d-flex flex-row align-items-center">
                                                    <span>RESOURCES</span>
                                                    <div className="d-flex flex-row align-items-center">
                                                        &nbsp;&nbsp;//&nbsp;&nbsp;
                                                        {allowCrossDispatching && (dispatchEquipmentPermission || dispatchEmployeePermission) && dispatchLocations.length > 1 ? (
                                                            <div className="resource-location-container">
                                                                <ValidatedSelect
                                                                    placeholder="Select Dispatching"
                                                                    id="selectedResourceDispatchLocation"
                                                                    name="selectedResourceDispatchLocation"
                                                                    styles={
                                                                        CompactSelectStyles
                                                                    }
                                                                    isClearable={
                                                                        false
                                                                    }
                                                                    className="resource-dropdown"
                                                                    options={
                                                                        dispatchLocations
                                                                    }
                                                                    value={
                                                                        resourceLocation
                                                                    }
                                                                    onChange={
                                                                        this
                                                                            .onSelectedResourceLocationChanged
                                                                    }
                                                                />
                                                            </div>
                                                        ) : (
                                                            <span className="p-0 m-0 text-muted">
                                                                {resourceLocation?.label}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div id="dispatchCenterContentHeader">
                                                    <div className="week-selection">
                                                        <ValidatedSelect
                                                            styles={
                                                                CompactSelectStyles
                                                            }
                                                            isClearable={
                                                                false
                                                            }
                                                            options={weeks}
                                                            getOptionLabel={(option) => option.range}
                                                            getOptionValue={(option) => option.start}
                                                            value={
                                                                selectedWeek ?? ''
                                                            }
                                                            onChange={
                                                                this.onCurrentWeekChanged
                                                            }
                                                        />
                                                    </div>

                                                    <div className="weekday-selection d-inline-flex align-items-center">
                                                        {!!selectedWeek
                                                            && !!(daysInRange ?? [])
                                                                .length && (
                                                                <ButtonGroup
                                                                    id="dispatchWeekdaySelection"
                                                                    size="sm"
                                                                >
                                                                    {daysInRange.map(
                                                                        (
                                                                            dayDate,
                                                                        ) => {
                                                                            const day = Weekdays.find(
                                                                                (
                                                                                    x,
                                                                                ) => x.id
                                                                                    === dayDate.getDay(),
                                                                            );
                                                                            return (
                                                                                <Button
                                                                                    key={
                                                                                        day.id
                                                                                    }
                                                                                    type="button"
                                                                                    color="outline-secondary"
                                                                                    onClick={(e) => this.onSelectedWeekRangeDayClicked(e, dayDate)}
                                                                                    className={cls(
                                                                                        {
                                                                                            active: (
                                                                                                selectedWeekRangeDays
                                                                                                ?? []
                                                                                            )
                                                                                                .map(
                                                                                                    (
                                                                                                        d,
                                                                                                    ) => d.toLocaleDateString(),
                                                                                                )
                                                                                                .includes(
                                                                                                    dayDate.toLocaleDateString(),
                                                                                                ),
                                                                                        },
                                                                                    )}
                                                                                >
                                                                                    <b>
                                                                                        {weekdayLetter(day.id)}
                                                                                    </b>
                                                                                </Button>
                                                                            );
                                                                        },
                                                                    )}
                                                                </ButtonGroup>
                                                            )}

                                                        {Boolean(tenantSettings.useRadioStyleDaySelection) && (
                                                            <>
                                                                <div
                                                                    id="weekdayHelpIcon"
                                                                    className="ml-2 text-muted cursor-pointer"
                                                                >
                                                                    <FontAwesomeIcon icon={faQuestionCircle} />
                                                                </div>
                                                                <UncontrolledTooltip target="weekdayHelpIcon">
                                                                    Click to select a day. Ctrl/Cmd-click to add or remove a day. Shift-click to select a range of days.
                                                                </UncontrolledTooltip>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div id="dispatchCenterContent">
                                                <div className="dispatch-content-toolbar">
                                                    <div className="dispatch-content-toolbar-search">
                                                        <Nav
                                                            id="dispatchResourceTabs"
                                                            tabs
                                                        >
                                                            {DispatchResourceTabs.map(
                                                                (tab) => {
                                                                    if (
                                                                        tab.number
                                                                        === DispatchResourceTabNumbers.Crews
                                                                        && !crewsEnabled
                                                                    ) return null;

                                                                    if (
                                                                        tab.name === 'Staff' &&
                                                                        dispatchLocations.length > 1 &&
                                                                        (!dispatchEmployeePermission && selectedResourceLocation != selectedJobDispatchLocation)
                                                                    ) {
                                                                        return null;
                                                                    } else if (
                                                                        tab.name === 'Equipment' &&
                                                                        dispatchLocations.length > 1 &&
                                                                        (!dispatchEquipmentPermission && selectedResourceLocation != selectedJobDispatchLocation)
                                                                    ) {
                                                                        return null;
                                                                    }

                                                                    return (
                                                                        <NavItem
                                                                            key={
                                                                                tab.number
                                                                            }
                                                                        >
                                                                            <NavLink
                                                                                className={cls(
                                                                                    {
                                                                                        active:
                                                                                            activeResourceTab.number
                                                                                            === tab.number,
                                                                                    },
                                                                                )}
                                                                                onClick={() => this.onResourceTabClicked(
                                                                                    tab,
                                                                                )}
                                                                            >
                                                                                <i
                                                                                    className={
                                                                                        `mr-2 fa ${tab.icon}`
                                                                                    }
                                                                                />
                                                                                <span className="tabName">
                                                                                    {
                                                                                        tab.name
                                                                                    }
                                                                                </span>
                                                                                {!!(
                                                                                    tab.number
                                                                                    === DispatchResourceTabNumbers.Crews
                                                                                )
                                                                                    && activeResourceTab.number
                                                                                    === DispatchResourceTabNumbers.Crews && (
                                                                                        <FontAwesomeIcon
                                                                                            id="crewButton"
                                                                                            title="Manage Crews"
                                                                                            size="lg"
                                                                                            onClick={() => {
                                                                                                const {
                                                                                                    crewsInterfaceOpen,
                                                                                                } = {
                                                                                                    ...this
                                                                                                        .state,
                                                                                                };
                                                                                                this.setState(
                                                                                                    {
                                                                                                        crewsInterfaceOpen:
                                                                                                            !crewsInterfaceOpen,
                                                                                                    },
                                                                                                );
                                                                                            }}
                                                                                            className="ml-2"
                                                                                            icon={
                                                                                                faCog
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                            </NavLink>
                                                                        </NavItem>
                                                                    );
                                                                },
                                                            )}
                                                        </Nav>

                                                        {!loading && (
                                                            <div id="dispatchCenterToolbar">

                                                                <div id="dispatchCenterToolbarRight">
                                                                    {(selectedResourceLocation === selectedJobDispatchLocation || Boolean(dispatchLocations.length === 1 || dispatchEmployeePermission)) && (
                                                                        <DispatchEmployeeFilter
                                                                            dailyAttendanceEvents={
                                                                                dailyAttendanceEvents
                                                                            }
                                                                            employeeDailyAttendanceEvents={
                                                                                employeeDailyAttendanceEvents
                                                                            }
                                                                            selectedWeekRangeDays={
                                                                                selectedWeekRangeDays
                                                                            }
                                                                            loading={loading}
                                                                            staffLoading={staffLoading}
                                                                            areAttendanceEventsEnabled={
                                                                                areAttendanceEventsEnabled
                                                                            }
                                                                            staff={currentResource}
                                                                            allJobs={[...allJobs, ...(resourceJobs || [])]}
                                                                            employeeLongTermStatuses={
                                                                                employeeLongTermStatuses
                                                                            }
                                                                            onlyShowAttendance={
                                                                                onlyShowAttendance
                                                                            }
                                                                            onlyShowUnassigned={
                                                                                onlyShowUnassigned
                                                                            }
                                                                            onlyShowCancelled={
                                                                                onlyShowCancelled
                                                                            }
                                                                            onAttendanceClick={
                                                                                this.onAttendanceClick
                                                                            }
                                                                            onUnassignedClick={
                                                                                this.onUnassignedClick
                                                                            }
                                                                            onCancelledClick={
                                                                                this.onCancelledClick
                                                                            }
                                                                            staffTabActive={
                                                                                staffTabActive
                                                                            }
                                                                            tenantSettings={
                                                                                tenantSettings
                                                                            }
                                                                            resourcesLoading={
                                                                                resourcesLoading
                                                                            }
                                                                            cancellationJobs={
                                                                                cancellationJobs
                                                                            }
                                                                            jobAssignmentCancellationTypes={
                                                                                jobAssignmentCancellationTypes
                                                                            }
                                                                        />
                                                                    )
                                                                    }

                                                                    {(selectedResourceLocation === selectedJobDispatchLocation || Boolean(dispatchLocations.length === 1 || dispatchEquipmentPermission)) && (
                                                                        <DispatchEquipmentFilter
                                                                            selectedWeekRangeDays={
                                                                                selectedWeekRangeDays
                                                                            }
                                                                            loading={loading}
                                                                            resourcesLoading={
                                                                                resourcesLoading
                                                                            }
                                                                            equipmentLoading={
                                                                                equipmentLoading
                                                                            }
                                                                            equipmentLongTermStatuses={equipmentLongTermStatuses}
                                                                            allJobs={[...allJobs, ...(resourceJobs || [])]}
                                                                            equipment={currentResource}
                                                                            onUnassignedEquipmentClick={
                                                                                this
                                                                                    .onUnassignedEquipmentClick
                                                                            }
                                                                            onlyShowUnassignedEquipment={
                                                                                onlyShowUnassignedEquipment
                                                                            }
                                                                            equipmentTabActive={
                                                                                equipmentTabActive
                                                                            }
                                                                            tenantSettings={
                                                                                tenantSettings
                                                                            }
                                                                        />
                                                                    )}

                                                                    <Input
                                                                        placeholder={`Search ${activeResourceTab.name}`}
                                                                        type="search"
                                                                        id="searchText"
                                                                        name="searchText"
                                                                        value={resourceSearchText ?? ''}
                                                                        onChange={(e) => this.handleResourceSearchText(
                                                                            e.target.value,
                                                                        )}
                                                                        startAddon={(
                                                                            <FontAwesomeIcon
                                                                                icon={faSearch}
                                                                                className="text-muted"
                                                                            />
                                                                        )}
                                                                        endAddon={(
                                                                            <IconButton onClick={this.toggleFilterModal}>
                                                                                <FontAwesomeIcon
                                                                                    icon={faFilter}
                                                                                    className="text-muted"
                                                                                />
                                                                            </IconButton>
                                                                        )}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="dispatch-content-toolbar-filters">
                                                    <div className="flex-fill" />

                                                    {filters.map((g) => {
                                                        let text = '';

                                                        if (g.filterType === FilterTypes.GROUP) {
                                                            text = g.groupIds
                                                                .map((id) => employeeGroups.find((eg) => eg.id === id)?.groupName)
                                                                .filter(Boolean)
                                                                .join(', ');
                                                        }

                                                        if (g.filterType === FilterTypes.EQUIPMENT_TYPE) {
                                                            text = g.equipIds
                                                                .map((id) => equipmentTypes.find((e) => e.id === id)?.description)
                                                                .filter(Boolean)
                                                                .join(', ');
                                                        }

                                                        if (g.filterType === FilterTypes.CERTIFICATION) {
                                                            text = g.certIds
                                                                .map((id) => certificationOptions.find((c) => c.id === id)?.name)
                                                                .filter(Boolean)
                                                                .join(', ');
                                                        }

                                                        if (text) {
                                                            return (
                                                                <Fragment key={g.filterType}>
                                                                    <Chip
                                                                        id={`filter-${g.filterType}`}
                                                                        text={text}
                                                                        className={cls('mt-2 filter-chip', { pulse: !!g.id && g.id === pulsingSlotId })}
                                                                        onRemove={() => this.removeFilter(g.filterType)}
                                                                        color="white"
                                                                    />

                                                                    <UncontrolledTooltip
                                                                        target={`filter-${g.filterType}`}
                                                                    >
                                                                        {text}
                                                                    </UncontrolledTooltip>
                                                                </Fragment>
                                                            );
                                                        }

                                                        return null;
                                                    })}
                                                </div>

                                                <Droppable
                                                    droppableId="resourcesDroppable"
                                                    style={{ transform: 'none' }}
                                                >
                                                    {(provided) => (
                                                        <dl
                                                            id="dispatchCenterContentArea"
                                                            {...provided.droppableProps}
                                                            ref={provided.innerRef}
                                                        >
                                                            {(!!staffLoading || !!resourcesLoading) && (
                                                                <CustomCircularProgress />
                                                            )}

                                                            {!staffLoading
                                                                && !resourcesLoading && (
                                                                    <>
                                                                        {staffTabActive
                                                                            && !staffLoading
                                                                            && (currentResource ?? {}).rows?.map(
                                                                                (
                                                                                    st,
                                                                                    index,
                                                                                ) => {
                                                                                    const isDisabled = this.isCardDisabled(st.employeeCertifications);
                                                                                    return (
                                                                                        <Draggable
                                                                                            key={`staff_${st.id}`}
                                                                                            draggableId={`staff_${st.id}`}
                                                                                            index={index}
                                                                                            isDragDisabled={isDisabled || !dispatchEditPermission}
                                                                                        >
                                                                                            {(
                                                                                                provided,
                                                                                            ) => (
                                                                                                <StaffCard
                                                                                                    innerRef={
                                                                                                        provided.innerRef
                                                                                                    }
                                                                                                    provided={
                                                                                                        provided
                                                                                                    }
                                                                                                    dragging={(this.state.draggableId ?? '').indexOf(st.id) !== -1}
                                                                                                    staff={
                                                                                                        st
                                                                                                    }
                                                                                                    jobs={
                                                                                                        resourceJobs
                                                                                                    }
                                                                                                    loading={Boolean(crossJobsLoading)}
                                                                                                    weekdays={
                                                                                                        daysInRange
                                                                                                    }
                                                                                                    minimal={
                                                                                                        `staff_${st.id}`
                                                                                                        === this
                                                                                                            .state
                                                                                                            .draggableId
                                                                                                    }
                                                                                                    longTermStatuses={
                                                                                                        longTermStatuses
                                                                                                    }
                                                                                                    employeeLongTermStatuses={
                                                                                                        employeeLongTermStatuses
                                                                                                    }
                                                                                                    dailyAttendanceEvents={
                                                                                                        dailyAttendanceEvents
                                                                                                    }
                                                                                                    employeeDailyAttendanceEvents={
                                                                                                        employeeDailyAttendanceEvents
                                                                                                    }
                                                                                                    cancellationJobs={
                                                                                                        crossDispatchCancellationJobs
                                                                                                    }
                                                                                                    hiddenStaffIds={
                                                                                                        hiddenStaffIds
                                                                                                    }
                                                                                                    jobAssignmentCancellationTypeIdsThatPreventReassignment={
                                                                                                        jobAssignmentCancellationTypeIdsThatPreventReassignment
                                                                                                    }
                                                                                                    jobAssignmentCancellationTypes={
                                                                                                        jobAssignmentCancellationTypes
                                                                                                    }
                                                                                                    tenantSettings={
                                                                                                        tenantSettings
                                                                                                    }
                                                                                                    disableStatus={
                                                                                                        isDisabled
                                                                                                    }
                                                                                                    certifications={
                                                                                                        certificationOptions
                                                                                                    }
                                                                                                    selected={
                                                                                                        this.state.selectedStaffForAssignment.includes(st.id)
                                                                                                    }
                                                                                                    onClick={() => this.handleStaffCardClick(st.id)}
                                                                                                    onDoubleClick={() => this.handleStaffCardDoubleClick(st.id)}
                                                                                                    selectedWeekRangeDays={selectedWeekRangeDays}
                                                                                                />
                                                                                            )}
                                                                                        </Draggable>
                                                                                    );
                                                                                },
                                                                            )}
                                                                        {staffTabActive && currentResource.rows.length === 0 && (
                                                                            <span>
                                                                                No
                                                                                staff
                                                                                found
                                                                                for
                                                                                the
                                                                                selected
                                                                                filters.
                                                                            </span>
                                                                        )}
                                                                        {equipmentTabActive
                                                                            && currentResource.rows.map(
                                                                                (
                                                                                    eq,
                                                                                    index,
                                                                                ) => (
                                                                                    <Draggable
                                                                                        key={`equipment_${eq.id}`}
                                                                                        draggableId={`equipment_${eq.id}`}
                                                                                        index={index}
                                                                                        isDragDisabled={!dispatchEditPermission}
                                                                                    >
                                                                                        {(
                                                                                            provided,
                                                                                        ) => (
                                                                                            <EquipmentCard
                                                                                                innerRef={
                                                                                                    provided.innerRef
                                                                                                }
                                                                                                provided={
                                                                                                    provided
                                                                                                }
                                                                                                equipment={
                                                                                                    eq
                                                                                                }
                                                                                                allJobs={
                                                                                                    resourceJobs
                                                                                                }
                                                                                                weekdays={
                                                                                                    daysInRange
                                                                                                }
                                                                                                loading={Boolean(crossJobsLoading)}
                                                                                                dragging={(this.state.draggableId ?? '').indexOf(eq.id) !== -1}
                                                                                                minimal={
                                                                                                    `equipment_${eq.id}`
                                                                                                    === this
                                                                                                        .state
                                                                                                        .draggableId
                                                                                                }
                                                                                                hiddenEquipmentIds={
                                                                                                    hiddenEquipmentIds
                                                                                                }
                                                                                                longTermStatuses={
                                                                                                    longTermStatuses
                                                                                                }
                                                                                                equipmentLongTermStatuses={
                                                                                                    equipmentLongTermStatuses
                                                                                                }
                                                                                                selectedWeekRangeDays={selectedWeekRangeDays}
                                                                                            />
                                                                                        )}
                                                                                    </Draggable>
                                                                                ),
                                                                            )}
                                                                        {equipmentTabActive && currentResource.rows.length === 0 && (
                                                                            <span>
                                                                                No
                                                                                equipment
                                                                                found
                                                                                for
                                                                                the
                                                                                selected
                                                                                dispatching.
                                                                            </span>
                                                                        )}
                                                                    </>
                                                                )}

                                                            {provided.placeholder}
                                                        </dl>
                                                    )}
                                                </Droppable>
                                            </div>

                                            {!!loading && <CustomCircularProgress />}
                                            {!loading && (
                                                <>


                                                    <div id="dispatchCenterFooter">
                                                        {!!((currentResource ?? {}).rows ?? []).length && (
                                                            <small>
                                                                {`Displaying ${(
                                                                    currentResource.rows
                                                                    ?? []
                                                                ).length
                                                                    } of ${currentResource.maxRows
                                                                    } resources (${currentResource.maxRows
                                                                    - currentResource.filterCount
                                                                    } filtered).${this.getHiddenResourceText()}`}
                                                            </small>
                                                        )}
                                                    </div>

                                                    {staffTabActive && this.state.selectedAttendanceEvent !== null && (
                                                        <AttendanceAssignmentFooter
                                                            isVisible={this.state.footerIsVisible}
                                                            selectedCount={this.state.selectedStaffForAssignment.length}
                                                            attendanceEventName={
                                                                (dailyAttendanceEvents.find(
                                                                    (e) => e.id === this.state.selectedAttendanceEvent,
                                                                ) || {}).name || 'Clear'
                                                            }
                                                            onCancel={this.handleAssignmentCancel}
                                                            onAssign={this.handleAssignmentConfirm}
                                                            isAssigning={this.state.isAssigningAttendance}
                                                            assignButtonLabel={
                                                                this.state.selectedAttendanceEvent === 0
                                                                    ? 'Clear Assignments'
                                                                    : 'Assign'
                                                            }
                                                            assigningButtonLabel={
                                                                this.state.selectedAttendanceEvent === 0
                                                                    ? 'Clearing...'
                                                                    : 'Assigning...'
                                                            }
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </Hide>

                                    <div
                                        id="dispatchRight"
                                        className={cls({
                                            'pointer-events-none':
                                                !!this.state.jobsLoading,
                                        })}
                                    >

                                        {!!this.state.loading && (
                                            <CustomCircularProgress />
                                        )}

                                        {!this.state.loading && (
                                            <>
                                                <Row className="toolbar align-items-center">
                                                    <Col xs={6}>
                                                        {Boolean(todaySelectedOrLater && dispatchEditPermission) && (
                                                            <Button
                                                                id="sendNotification"
                                                                type="button"
                                                                color="primary"
                                                                className="text-nowrap"
                                                                onClick={this.toggleModal}
                                                            >
                                                                <FontAwesomeIcon
                                                                    size="lg"
                                                                    icon={faBolt}
                                                                    className="mr-2"
                                                                />

                                                                Send Notifications
                                                            </Button>
                                                        )}
                                                    </Col>
                                                    <Col
                                                        xs={6}
                                                        className="d-flex flex-row align-items-center justify-content-end"
                                                    >
                                                        <ValidatedSelect
                                                            placeholder="Select Dispatching"
                                                            id="selectedJobDispatchLocation"
                                                            name="selectedJobDispatchLocation"
                                                            styles={
                                                                CompactSelectStyles
                                                            }
                                                            isClearable={
                                                                false
                                                            }
                                                            options={
                                                                dispatchLocations
                                                            }
                                                            value={
                                                                jobDispatchLocation
                                                            }
                                                            onChange={
                                                                this
                                                                    .onSelectedJobDispatchLocationChanged
                                                            }
                                                        />

                                                        {!!window.localStorage && this.showPreferredDispatch() && (
                                                            <FontAwesomeIcon
                                                                icon={
                                                                    faStar
                                                                }
                                                                onClick={() => {
                                                                    if (
                                                                        selectedJobDispatchLocation
                                                                    ) {
                                                                        this.preferredDispatch(
                                                                            selectedJobDispatchLocation,
                                                                        );
                                                                    }
                                                                }}
                                                                title="Toggle this as preferred Dispatching"
                                                                size="lg"
                                                                className={
                                                                    `preferred-dispatch-icon cursor-pointer ml-2 ${selectedJobDispatchLocation
                                                                        && this
                                                                            .state
                                                                            .preferredDispatchLocation
                                                                        === selectedJobDispatchLocation
                                                                        ? 'active'
                                                                        : 'text-muted'}`
                                                                }
                                                            />
                                                        )}
                                                    </Col>
                                                </Row>

                                                <Hide smDown>
                                                    <FlexCenterRow className="mobile-date-header align-items-center mt-3 mb-3">
                                                        <IconButton
                                                            onClick={() => this.handleMobileDateNavigation(-1)}
                                                            disabled={!this.canNavigateMobile(-1)}
                                                            size="lg"
                                                            className="mr-2"
                                                        >
                                                            <FontAwesomeIcon
                                                                size="lg"
                                                                icon={faChevronLeft}
                                                            />
                                                        </IconButton>

                                                        <DatePicker
                                                            selected={selectedWeekRangeDays[0]}
                                                            onChange={this.handleMobileDateSelection}
                                                            calendarStartDay={1}
                                                            maxDate={weeks.length > 0 ? moment(weeks[weeks.length - 1].end).toDate() : null}
                                                            customInput={(
                                                                <h2
                                                                    className="mobile-date-picker mb-0"
                                                                    style={{ cursor: 'pointer', WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
                                                                >
                                                                    {moment(selectedWeekRangeDays[0]).format('dddd, MMM DD')}
                                                                </h2>
                                                            )}
                                                            popperPlacement="bottom"
                                                            preventOverflow
                                                            popperModifiers={{
                                                                preventOverflow: {
                                                                    enabled: true,
                                                                    boundariesElement: 'viewport',
                                                                },
                                                            }}
                                                        />

                                                        <IconButton
                                                            onClick={() => this.handleMobileDateNavigation(1)}
                                                            disabled={!this.canNavigateMobile(1)}
                                                            size="lg"
                                                            className="ml-2"
                                                        >
                                                            <FontAwesomeIcon
                                                                size="lg"
                                                                icon={faChevronRight}
                                                            />
                                                        </IconButton>
                                                    </FlexCenterRow>
                                                </Hide>

                                                <Hide mdUp>
                                                    <small
                                                        className="text-success mb-1 w-100 text-right d-none d-md-block"
                                                        hidden={!this.state.lastPushUpdate}
                                                    >
                                                        {this.state.lastPushUpdate}
                                                    </small>
                                                </Hide>

                                                <div id="dispatchRightContent">
                                                    <QuickJobCreatedAlert
                                                        jobId={quickJobCreatedJobId}
                                                    />

                                                    <Nav
                                                        id="dispatchJobTabs"
                                                        tabs
                                                    >
                                                        {DispatchJobTabs
                                                            .filter((tab) => tab.number < 4 || timesheetCheckinsEnabled)
                                                            .map((tab) => (
                                                                <NavItem
                                                                    key={
                                                                        tab.number
                                                                    }
                                                                >
                                                                    <NavLink
                                                                        className={cls(
                                                                            {
                                                                                active:
                                                                                    activeJobTab.number
                                                                                    === tab.number,
                                                                            },
                                                                        )}
                                                                        onClick={() => this.onJobTabClicked(
                                                                            tab,
                                                                        )}
                                                                    >
                                                                        {tab.name}
                                                                    </NavLink>
                                                                </NavItem>
                                                            ),
                                                            )}
                                                    </Nav>
                                                    {activeJobTab.number === DispatchJobTabNumbers.Planning && (
                                                        <div className="planning-content">
                                                            <PlanningBoard
                                                                planningBoard={planningBoard}
                                                                handlePlanningStatusToggle={this.handlePlanningStatusToggle}
                                                            />

                                                            <div className="job-listing-container">
                                                                <div className="job-listing-container-header">
                                                                    <div />
                                                                    <div className="job-listing-filters">
                                                                        {selectedPlanningStatus && (
                                                                            <Chip
                                                                                key={selectedPlanningStatus}
                                                                                text={selectedPlanningStatus}
                                                                                className={cls('mr-2 text-uppercase text-small', {
                                                                                    unfilled: selectedPlanningStatus === JobStatusTypes.UNFILLED,
                                                                                    scheduled: selectedPlanningStatus === JobStatusTypes.SCHEDULED,
                                                                                    noResponse: selectedPlanningStatus === JobStatusTypes.NO_RESPONSE,
                                                                                    confirmed: selectedPlanningStatus === JobStatusTypes.CONFIRMED,
                                                                                })}
                                                                                onRemove={() => this.handlePlanningStatusToggle(selectedPlanningStatus)}
                                                                                color="white"
                                                                            />
                                                                        )}

                                                                        <Input
                                                                            placeholder="Search Jobs"
                                                                            type="search"
                                                                            id="jobSearchText"
                                                                            name="jobSearchText"
                                                                            startAddonClassName="addon-centered"
                                                                            value={
                                                                                jobSearchText
                                                                                ?? ''
                                                                            }
                                                                            onChange={(
                                                                                e,
                                                                            ) => this.handleJobSearchText(
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )}
                                                                            startAddon={(
                                                                                <FontAwesomeIcon
                                                                                    icon={faSearch}
                                                                                    className="text-muted"
                                                                                />
                                                                            )}
                                                                        />

                                                                        <IconButton
                                                                            className="ml-2"
                                                                            title="Expand or collapse all jobs."
                                                                            onClick={() => {
                                                                                const {
                                                                                    planningJobs,
                                                                                    allJobsCollapsed,
                                                                                } = {
                                                                                    ...this
                                                                                        .state,
                                                                                };
                                                                                const ids = planningJobs.map(
                                                                                    (
                                                                                        x,
                                                                                    ) => x.id,
                                                                                );
                                                                                this.setState(
                                                                                    {
                                                                                        allJobsCollapsed:
                                                                                            !allJobsCollapsed,
                                                                                        jobsOpened:
                                                                                            allJobsCollapsed
                                                                                                ? []
                                                                                                : ids,
                                                                                    },
                                                                                );
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={allJobsCollapsed ? faChevronUp : faChevronDown}
                                                                                size="lg"
                                                                            />
                                                                        </IconButton>
                                                                    </div>
                                                                </div>
                                                                <div className="job-listing">
                                                                    {!!this.state
                                                                        .jobsLoading && (
                                                                            <CustomCircularProgress />
                                                                        )}

                                                                    {!(filteredJobs ?? []).length && (
                                                                        <span>
                                                                            No jobs
                                                                            with
                                                                            assignments
                                                                            found
                                                                            for the
                                                                            selected
                                                                            filters.
                                                                        </span>
                                                                    )}

                                                                    {!this.state
                                                                        .jobsLoading
                                                                        && !!(
                                                                            filteredJobs
                                                                            ?? []
                                                                        ).length
                                                                        && filteredJobs.map(
                                                                            (
                                                                                job,
                                                                            ) => {
                                                                                const jobStartTime = appendTimeZone(job.startTime, job.startTimeOffset, job.timeZone);
                                                                                // let cancelled = selectedWeekRangeDays.every(x => job.assignments[x.getDay()].isCancelled === true);
                                                                                // if (!cancelled) {

                                                                                const county = getCounty(this.state.usCounties, {
                                                                                    usCountyId: job.usCounty?.id,
                                                                                    contractUnionWorkTypeId: job.contractUnionWorkTypeId,
                                                                                });
                                                                                return (
                                                                                    <ControlledAccordionCollapse
                                                                                        key={job.id}
                                                                                        open={this.jobIsOpened(job.id)}
                                                                                        setOpen={this.toggleJobCollapse(job.id)}
                                                                                        CollapseProps={{ className: 'd-flex relative' }}
                                                                                        className={job.hasCustomForms && !job.formsCompleted && !job.isEmergency ? 'border-danger' : ''}
                                                                                        secondaryAction={(
                                                                                            <Link
                                                                                                className="job-number ml-1 mr-2 site-link"
                                                                                                to={job.projectId ? `${AppNavPaths.Project}/${job.projectId}?openJob=${job.id}` : `${AppNavPaths.Job}/${job.id}`}
                                                                                                target="_blank"
                                                                                            >
                                                                                                {job.number}
                                                                                            </Link>
                                                                                        )}
                                                                                        title={evaluateJobTitle(tenantSettings?.jobTitleTemplate, job, jobStartTime)}
                                                                                    >
                                                                                        <div
                                                                                            className={cls('job-details', {
                                                                                                saving: !!jobsSaving.includes(
                                                                                                    job.id,
                                                                                                ),
                                                                                            })}
                                                                                        >
                                                                                            <div className="job-details-header">
                                                                                                <Block
                                                                                                    component={Container}
                                                                                                    className="d-flex flex-column"
                                                                                                    square
                                                                                                >
                                                                                                    <Row className="m-0">
                                                                                                        <Col
                                                                                                            xs={6}
                                                                                                            className="pl-0"
                                                                                                        >
                                                                                                            <FormLabel label="Location" />

                                                                                                            <div
                                                                                                                className="job-address mb-1"
                                                                                                                title="Meeting Location"
                                                                                                            >
                                                                                                                <Badge
                                                                                                                    color="secondary"
                                                                                                                    className="mr-1"
                                                                                                                >M</Badge>
                                                                                                                {job.meetingAddress}
                                                                                                            </div>

                                                                                                            <div
                                                                                                                className="job-address"
                                                                                                                title="Job Location"
                                                                                                            >
                                                                                                                <Badge
                                                                                                                    color="secondary"
                                                                                                                    className="mr-1"
                                                                                                                >J</Badge>
                                                                                                                {job.jobAddress}
                                                                                                            </div>

                                                                                                            {Boolean(job.usCounty) && (
                                                                                                                <div
                                                                                                                    className="job-address mt-1"
                                                                                                                    title="Job County"
                                                                                                                >
                                                                                                                    <Badge
                                                                                                                        color="secondary"
                                                                                                                        className="mr-1"
                                                                                                                    >C</Badge>
                                                                                                                    <CountyLabel {...county} />
                                                                                                                </div>
                                                                                                            )}

                                                                                                            {job.hasCustomForms && (
                                                                                                                <CustomForms
                                                                                                                    label="Job Forms"
                                                                                                                    className="p-0 mt-1"
                                                                                                                    isEmergency={job.isEmergency}
                                                                                                                    jobId={job.id}
                                                                                                                    companyId={job.companyId}
                                                                                                                    formContextId={CustomFormContext.Job}
                                                                                                                    onCompletionChange={(completed) => this.onFormCompletionChange(job.id, completed)}
                                                                                                                />
                                                                                                            )}
                                                                                                        </Col>

                                                                                                        {job?.jobTypeId !== 1 && (
                                                                                                            <Col
                                                                                                                xs={6}
                                                                                                                className="pr-0"
                                                                                                            >
                                                                                                                <FormLabel label="Job Type" />
                                                                                                                <div className="text-muted text-small font-weight-bold">{job.jobTypeDescription}</div>
                                                                                                            </Col>
                                                                                                        )}
                                                                                                    </Row>
                                                                                                </Block>
                                                                                            </div>

                                                                                            <DispatchJobAssignments
                                                                                                onCertificationButtonClick={this.handleCertificationButtonClick}
                                                                                                filters={filters}
                                                                                                allStaff={staff.rows}
                                                                                                allEquipment={equipment.rows}
                                                                                                job={job}
                                                                                                selectedWeekRangeDays={selectedWeekRangeDays}
                                                                                                onEquipmentAssignmentChange={this.onEquipmentAssignmentChange}
                                                                                                onRemoveAssignment={this.onRemoveAssignment}
                                                                                                onRemoveDayJob={this.onRemoveDayJob}
                                                                                                onRefreshDayJobs={this.onRefreshDayJobs}
                                                                                                dispatchLocations={dispatchLocations}
                                                                                                crossDispatchPermission={crossDispatchPermission}
                                                                                                setCrewLead={this.setCrewLead}
                                                                                                onEditJobWorkflowClicked={this.onEditJobWorkflowClicked}
                                                                                                tenantSettings={tenantSettings}
                                                                                                dragging={dragging}
                                                                                                isDropZoneValid={this.isDropZoneValid}
                                                                                                onSlotSelect={this.handleSlotSelect}
                                                                                                pulsingSlotId={pulsingSlotId}
                                                                                                dispatchEditPermission={dispatchEditPermission}
                                                                                                equipmentAuditLogPermission={equipmentAuditLogPermission}
                                                                                                onMobileAssignmentClick={this.openMobileAssignmentModal}
                                                                                                getEligibleStaffForEquipment={getEligibleStaffForEquipment}
                                                                                                getEligibleEquipmentForEquipment={getEligibleEquipmentForEquipment}
                                                                                            />

                                                                                            <div className="saving-progress">
                                                                                                <FontAwesomeIcon
                                                                                                    icon={
                                                                                                        faCircleNotch
                                                                                                    }
                                                                                                    spin
                                                                                                />
                                                                                            </div>
                                                                                        </div>
                                                                                    </ControlledAccordionCollapse>
                                                                                );
                                                                                // }
                                                                            },
                                                                        )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {activeJobTab.number
                                                        === DispatchJobTabNumbers.Cancellations && (
                                                            <div className="planning-content">
                                                                <div className="scoreboard">
                                                                    <table className="table table-sm table-bordered border-top-0">
                                                                        <thead>
                                                                            <tr>
                                                                                <th className="text-center" />
                                                                                <th className="text-center assignment-status-label cancelPending">
                                                                                    <Button
                                                                                        size="sm"
                                                                                        color="link"
                                                                                        onClick={() => this.handleCancellationStatusToggle(JobStatusTypes.CANCEL_PENDING)}
                                                                                    >
                                                                                        <FontAwesomeIcon
                                                                                            icon={faMagnifyingGlass}
                                                                                            className="mr-1"
                                                                                        />
                                                                                        <span>{JobStatusTypes.CANCEL_PENDING}</span>
                                                                                    </Button>
                                                                                </th>
                                                                                <th className="text-center assignment-status-label cancelConfirmed">
                                                                                    <Button
                                                                                        size="sm"
                                                                                        color="link"
                                                                                        onClick={() => this.handleCancellationStatusToggle(JobStatusTypes.CANCEL_CONFIRMED)}
                                                                                    >
                                                                                        <FontAwesomeIcon
                                                                                            icon={faMagnifyingGlass}
                                                                                            className="mr-1"
                                                                                        />
                                                                                        <span>{JobStatusTypes.CANCEL_CONFIRMED}</span>
                                                                                    </Button>
                                                                                </th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            <tr>
                                                                                <td className="text-center font-bold">
                                                                                    <FontAwesomeIcon icon={faUser} />
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    {
                                                                                        cancellationBoard
                                                                                            .staff
                                                                                            .cancelPending
                                                                                    }
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    {
                                                                                        cancellationBoard
                                                                                            .staff
                                                                                            .cancelConfirmed
                                                                                    }
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="text-center font-bold">
                                                                                    <FontAwesomeIcon icon={faTruck} />
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    {
                                                                                        cancellationBoard
                                                                                            .equipment
                                                                                            .cancelPending
                                                                                    }
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    {
                                                                                        cancellationBoard
                                                                                            .equipment
                                                                                            .cancelConfirmed
                                                                                    }
                                                                                </td>
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                                <div className="job-listing-container">
                                                                    <div className="job-listing-container-header">
                                                                        <div />
                                                                        <div className="job-listing-filters">
                                                                            {selectedCancellationStatus && (
                                                                                <Chip
                                                                                    key={selectedCancellationStatus}
                                                                                    text={selectedCancellationStatus}
                                                                                    className={cls('mr-2 text-uppercase text-small', {
                                                                                        cancelPending: selectedCancellationStatus === JobStatusTypes.CANCEL_PENDING,
                                                                                        cancelConfirmed: selectedCancellationStatus === JobStatusTypes.CANCEL_CONFIRMED,
                                                                                    })}
                                                                                    onRemove={() => this.handleCancellationStatusToggle(selectedCancellationStatus)}
                                                                                    color="white"
                                                                                />
                                                                            )}
                                                                            <Input
                                                                                placeholder="Search Cancellations"
                                                                                size="sm"
                                                                                type="search"
                                                                                id="cancellationJobSearchText"
                                                                                name="cancellationJobSearchText"
                                                                                value={
                                                                                    cancellationJobSearchText
                                                                                    ?? ''
                                                                                }
                                                                                startAddon={(
                                                                                    <FontAwesomeIcon
                                                                                        icon={faSearch}
                                                                                        className="text-muted"
                                                                                    />
                                                                                )}
                                                                                onChange={(
                                                                                    e,
                                                                                ) => this.handleCancellationJobSearchText(
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                )}
                                                                            />
                                                                            <IconButton
                                                                                className="ml-2"
                                                                                title="Expand or collapse all cancellations."
                                                                                onClick={() => {
                                                                                    const {
                                                                                        cancellationJobs,
                                                                                        allCancellationJobsCollapsed,
                                                                                    } = {
                                                                                        ...this
                                                                                            .state,
                                                                                    };
                                                                                    const ids = cancellationJobs.map(
                                                                                        (
                                                                                            x,
                                                                                        ) => x.id,
                                                                                    );
                                                                                    this.setState(
                                                                                        {
                                                                                            allCancellationJobsCollapsed:
                                                                                                !allCancellationJobsCollapsed,
                                                                                            jobsOpened:
                                                                                                allCancellationJobsCollapsed
                                                                                                    ? []
                                                                                                    : ids,
                                                                                        },
                                                                                    );
                                                                                }}
                                                                            >
                                                                                <FontAwesomeIcon
                                                                                    icon={allCancellationJobsCollapsed ? faChevronUp : faChevronDown}
                                                                                    size="lg"
                                                                                />
                                                                            </IconButton>
                                                                        </div>
                                                                    </div>
                                                                    <div className="job-listing">
                                                                        {!!this.state
                                                                            .cancellationsLoading && (
                                                                                <CustomCircularProgress />
                                                                            )}
                                                                        {!(filteredCancellationJobs ?? []).length && (
                                                                            <span>
                                                                                No
                                                                                cancellations
                                                                                found
                                                                                for the
                                                                                selected
                                                                                filters.
                                                                            </span>
                                                                        )}

                                                                        {!this.state
                                                                            .cancellationsLoading
                                                                            && !!(
                                                                                filteredCancellationJobs
                                                                                ?? []
                                                                            ).length
                                                                            && filteredCancellationJobs.map(
                                                                                (job) => {
                                                                                    const jobStartTime = appendTimeZone(job.startTime, job.startTimeOffset, job.timeZone);
                                                                                    const county = getCounty(this.state.usCounties, {
                                                                                        usCountyId: job.usCounty?.id,
                                                                                        contractUnionWorkTypeId: job.contractUnionWorkTypeId,
                                                                                    });

                                                                                    return (
                                                                                        <ControlledAccordionCollapse
                                                                                            key={job.id}
                                                                                            open={this.jobIsOpened(job.id)}
                                                                                            setOpen={this.toggleJobCollapse(job.id)}
                                                                                            CollapseProps={{ className: 'd-flex relative' }}
                                                                                            secondaryAction={(
                                                                                                <Link
                                                                                                    className="job-number ml-1 mr-2 site-link"
                                                                                                    to={job.projectId ? `${AppNavPaths.Project}/${job.projectId}?openJob=${job.id}` : `${AppNavPaths.Job}/${job.id}`}
                                                                                                    target="_blank"
                                                                                                >
                                                                                                    {job.number}
                                                                                                </Link>
                                                                                            )}
                                                                                            title={evaluateJobTitle(tenantSettings?.jobTitleTemplate, job, jobStartTime)}
                                                                                        >
                                                                                            <div
                                                                                                className={cls('job-details', {
                                                                                                    saving: !!jobsSaving.includes(
                                                                                                        job.id,
                                                                                                    ),
                                                                                                })}
                                                                                            >
                                                                                                <div className="job-details-header">
                                                                                                    <Block
                                                                                                        component={Container}
                                                                                                        className="d-flex flex-column"
                                                                                                        square
                                                                                                    >
                                                                                                        <Row>
                                                                                                            <Col xs={6}>
                                                                                                                <FormLabel
                                                                                                                    label="Location"
                                                                                                                />

                                                                                                                <span
                                                                                                                    className="job-address mb-1"
                                                                                                                    title="Meeting Location"
                                                                                                                >
                                                                                                                    <Badge
                                                                                                                        color="secondary"
                                                                                                                        className="mr-1"
                                                                                                                    >
                                                                                                                        M
                                                                                                                    </Badge>

                                                                                                                    {job.meetingAddress}
                                                                                                                </span>
                                                                                                                <span
                                                                                                                    className="job-address"
                                                                                                                    title="Job Location"
                                                                                                                >
                                                                                                                    <Badge
                                                                                                                        color="secondary"
                                                                                                                        className="mr-1"
                                                                                                                    >
                                                                                                                        J
                                                                                                                    </Badge>

                                                                                                                    {job.jobAddress}
                                                                                                                </span>

                                                                                                                {Boolean(job.usCounty) && (
                                                                                                                    <span
                                                                                                                        className="job-address mt-1"
                                                                                                                        title="Job County"
                                                                                                                    >
                                                                                                                        <Badge
                                                                                                                            color="secondary"
                                                                                                                            className="mr-1"
                                                                                                                        >
                                                                                                                            C
                                                                                                                        </Badge>

                                                                                                                        <CountyLabel {...county} />
                                                                                                                    </span>
                                                                                                                )}
                                                                                                            </Col>
                                                                                                        </Row>
                                                                                                    </Block>
                                                                                                </div>
                                                                                                <DispatchCancellationJobAssignments
                                                                                                    job={
                                                                                                        job
                                                                                                    }
                                                                                                    selectedWeekRangeDays={
                                                                                                        selectedWeekRangeDays
                                                                                                    }
                                                                                                    tenantSettings={
                                                                                                        tenantSettings
                                                                                                    }
                                                                                                    onViewJobWorkflowClicked={this.onViewJobWorkflowClicked}
                                                                                                />

                                                                                                <div className="saving-progress">
                                                                                                    <FontAwesomeIcon
                                                                                                        icon={
                                                                                                            faCircleNotch
                                                                                                        }
                                                                                                        spin
                                                                                                    />
                                                                                                </div>
                                                                                            </div>
                                                                                        </ControlledAccordionCollapse>
                                                                                    );
                                                                                },
                                                                            )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    {activeJobTab.number === DispatchJobTabNumbers.Events && (
                                                        <div className="planning-content">
                                                            <div className="scoreboard">
                                                                <table className="table table-sm table-bordered border-top-0">
                                                                    <thead>
                                                                        <tr>
                                                                            <th className="text-center" />

                                                                            <th className="text-center assignment-status-label confirmed">
                                                                                <Button
                                                                                    size="sm"
                                                                                    color="link"
                                                                                    onClick={() => this.handleEventStatusToggle(EventStatus.ShiftStart)}
                                                                                >
                                                                                    <FontAwesomeIcon
                                                                                        icon={faMagnifyingGlass}
                                                                                        className="mr-1"
                                                                                    />
                                                                                    <span>{EventStatus.ShiftStart}</span>
                                                                                </Button>
                                                                            </th>
                                                                            <th className="text-center assignment-status-label confirmed">
                                                                                <Button
                                                                                    size="sm"
                                                                                    color="link"
                                                                                    onClick={() => this.handleEventStatusToggle(EventStatus.JobStart)}
                                                                                >
                                                                                    <FontAwesomeIcon
                                                                                        icon={faMagnifyingGlass}
                                                                                        className="mr-1"
                                                                                    />
                                                                                    <span>{EventStatus.JobStart}</span>
                                                                                </Button>
                                                                            </th>
                                                                            <th className="text-center assignment-status-label confirmed">
                                                                                <Button
                                                                                    size="sm"
                                                                                    color="link"
                                                                                    onClick={() => this.handleEventStatusToggle(EventStatus.JobEnd)}
                                                                                >
                                                                                    <FontAwesomeIcon
                                                                                        icon={faMagnifyingGlass}
                                                                                        className="mr-1"
                                                                                    />
                                                                                    <span>{EventStatus.JobEnd}</span>
                                                                                </Button>
                                                                            </th>
                                                                            <th className="text-center assignment-status-label confirmed">
                                                                                <Button
                                                                                    size="sm"
                                                                                    color="link"
                                                                                    onClick={() => this.handleEventStatusToggle(EventStatus.ShiftEnd)}
                                                                                >
                                                                                    <FontAwesomeIcon
                                                                                        icon={faMagnifyingGlass}
                                                                                        className="mr-1"
                                                                                    />
                                                                                    <span>{EventStatus.ShiftEnd}</span>
                                                                                </Button>
                                                                            </th>
                                                                            <th className="text-center assignment-status-label noResponse">
                                                                                <Button
                                                                                    size="sm"
                                                                                    color="link"
                                                                                    onClick={() => this.handleEventStatusToggle(EventStatus.NoShow)}
                                                                                >
                                                                                    <FontAwesomeIcon
                                                                                        icon={faMagnifyingGlass}
                                                                                        className="mr-1"
                                                                                    />
                                                                                    <span>{EventStatus.NoShow}</span>
                                                                                </Button>
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        <tr>
                                                                            <td className="text-center font-bold">
                                                                                <FontAwesomeIcon icon={faUser} />
                                                                            </td>
                                                                            <td className="text-center">
                                                                                {
                                                                                    eventBoard.shiftStart
                                                                                }
                                                                            </td>
                                                                            <td className="text-center">
                                                                                {
                                                                                    eventBoard.jobStart
                                                                                }
                                                                            </td>
                                                                            <td className="text-center">
                                                                                {
                                                                                    eventBoard.jobEnd
                                                                                }
                                                                            </td>
                                                                            <td className="text-center">
                                                                                {
                                                                                    eventBoard.shiftEnd
                                                                                }
                                                                            </td>
                                                                            <td className="text-center">
                                                                                {
                                                                                    eventBoard.noShow
                                                                                }
                                                                            </td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                            <div className="job-listing-container">
                                                                <div className="job-listing-container-header">
                                                                    <div />
                                                                    <div className="job-listing-filters">
                                                                        {selectedEventStatus && (
                                                                            <Chip
                                                                                key={selectedEventStatus}
                                                                                text={selectedEventStatus}
                                                                                className={cls('mr-2 text-uppercase text-small', {
                                                                                    noResponse: selectedEventStatus === EventStatus.NoShow,
                                                                                    confirmed: selectedEventStatus !== EventStatus.NoShow,
                                                                                })}
                                                                                onRemove={() => this.handleEventStatusToggle(selectedEventStatus)}
                                                                                color="white"
                                                                            />
                                                                        )}
                                                                        <Input
                                                                            placeholder="Search Jobs"
                                                                            size="sm"
                                                                            type="search"
                                                                            id="jobSearchText"
                                                                            name="jobSearchText"
                                                                            value={
                                                                                jobSearchText
                                                                                ?? ''
                                                                            }
                                                                            onChange={(
                                                                                e,
                                                                            ) => this.handleJobSearchText(
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )}
                                                                            startAddon={(
                                                                                <FontAwesomeIcon
                                                                                    icon={faSearch}
                                                                                    className="text-muted"
                                                                                />
                                                                            )}
                                                                        />
                                                                        <IconButton
                                                                            className="ml-2"
                                                                            title="Expand or collapse all jobs."
                                                                            onClick={() => {
                                                                                const {
                                                                                    planningJobs,
                                                                                    allJobsCollapsed,
                                                                                } = {
                                                                                    ...this
                                                                                        .state,
                                                                                };
                                                                                const ids = planningJobs.map(
                                                                                    (
                                                                                        x,
                                                                                    ) => x.id,
                                                                                );
                                                                                this.setState(
                                                                                    {
                                                                                        allJobsCollapsed:
                                                                                            !allJobsCollapsed,
                                                                                        jobsOpened:
                                                                                            allJobsCollapsed
                                                                                                ? []
                                                                                                : ids,
                                                                                    },
                                                                                );
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={allJobsCollapsed ? faChevronUp : faChevronDown}
                                                                                size="lg"
                                                                            />
                                                                        </IconButton>
                                                                    </div>
                                                                </div>
                                                                <div className="job-listing">
                                                                    {!!this.state
                                                                        .jobsLoading && (
                                                                            <CustomCircularProgress />
                                                                        )}
                                                                    {!(filteredevents ?? []).length && (
                                                                        <span>
                                                                            No
                                                                            Jobs
                                                                            found
                                                                            for the
                                                                            selected
                                                                            filters.
                                                                        </span>
                                                                    )}
                                                                    {!this.state
                                                                        .jobsLoading
                                                                        && !!(
                                                                            filteredevents
                                                                            ?? []
                                                                        ).length
                                                                        && filteredevents.map(
                                                                            (job) => {
                                                                                const jobStartTime = appendTimeZone(job.startTime, job.startTimeOffset, job.timeZone);
                                                                                const county = getCounty(this.state.usCounties, {
                                                                                    usCountyId: job.usCounty?.id,
                                                                                    contractUnionWorkTypeId: job.contractUnionWorkTypeId,
                                                                                });

                                                                                return (
                                                                                    <ControlledAccordionCollapse
                                                                                        key={job.id}
                                                                                        open={this.jobIsOpened(job.id)}
                                                                                        setOpen={this.toggleJobCollapse(job.id)}
                                                                                        CollapseProps={{ className: 'd-flex relative' }}
                                                                                        secondaryAction={(
                                                                                            <Link
                                                                                                className="job-number ml-1 mr-2 site-link"
                                                                                                to={job.projectId ? `${AppNavPaths.Project}/${job.projectId}?openJob=${job.id}` : `${AppNavPaths.Job}/${job.id}`}
                                                                                                target="_blank"
                                                                                            >
                                                                                                {job.number}
                                                                                            </Link>
                                                                                        )}
                                                                                        title={evaluateJobTitle(tenantSettings?.jobTitleTemplate, job, jobStartTime)}
                                                                                    >
                                                                                        <div
                                                                                            className={cls('job-details', {
                                                                                                saving: !!jobsSaving.includes(
                                                                                                    job.id,
                                                                                                ),
                                                                                            })}
                                                                                        >
                                                                                            <div className="job-details-header">
                                                                                                <Block
                                                                                                    component={Container}
                                                                                                    className="d-flex flex-column"
                                                                                                    square
                                                                                                >
                                                                                                    <Row>
                                                                                                        <Col xs={6}>
                                                                                                            <FormLabel
                                                                                                                label="Location"
                                                                                                            />

                                                                                                            <span
                                                                                                                className="job-address mb-1"
                                                                                                                title="Meeting Location"
                                                                                                            >
                                                                                                                <Badge
                                                                                                                    color="secondary"
                                                                                                                    className="mr-1"
                                                                                                                >
                                                                                                                    M
                                                                                                                </Badge>

                                                                                                                {job.meetingAddress}
                                                                                                            </span>
                                                                                                            <span
                                                                                                                className="job-address"
                                                                                                                title="Job Location"
                                                                                                            >
                                                                                                                <Badge
                                                                                                                    color="secondary"
                                                                                                                    className="mr-1"
                                                                                                                >
                                                                                                                    J
                                                                                                                </Badge>

                                                                                                                {job.jobAddress}
                                                                                                            </span>

                                                                                                            {Boolean(job.usCounty) && (
                                                                                                                <span
                                                                                                                    className="job-address mt-1"
                                                                                                                    title="Job County"
                                                                                                                >
                                                                                                                    <Badge
                                                                                                                        color="secondary"
                                                                                                                        className="mr-1"
                                                                                                                    >
                                                                                                                        C
                                                                                                                    </Badge>

                                                                                                                    <CountyLabel {...county} />
                                                                                                                </span>
                                                                                                            )}
                                                                                                        </Col>
                                                                                                    </Row>
                                                                                                </Block>
                                                                                            </div>
                                                                                            <DispatchEmployeeEvents
                                                                                                job={
                                                                                                    job
                                                                                                }
                                                                                                selectedWeekRangeDays={
                                                                                                    selectedWeekRangeDays
                                                                                                }
                                                                                                tenantSettings={
                                                                                                    tenantSettings
                                                                                                }
                                                                                            />

                                                                                            <div className="saving-progress">
                                                                                                <FontAwesomeIcon
                                                                                                    icon={
                                                                                                        faCircleNotch
                                                                                                    }
                                                                                                    spin
                                                                                                />
                                                                                            </div>
                                                                                        </div>
                                                                                    </ControlledAccordionCollapse>
                                                                                );
                                                                            },
                                                                        )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                <Hide mdUp>
                                                    {activeJobTab.number === DispatchJobTabNumbers.Planning && (
                                                        <div id="dispatchRightContentFooter">
                                                            {!!(planningJobs ?? [])
                                                                .length && (
                                                                    <small>
                                                                        {`Displaying ${planningJobs.length
                                                                            } of ${planningJobsMaxRows} jobs (${planningJobsMaxRows
                                                                            - planningJobsFilteredCount
                                                                            } filtered).`}
                                                                    </small>
                                                                )}
                                                        </div>
                                                    )}

                                                    {activeJobTab.number === DispatchJobTabNumbers.Cancellations && (
                                                        <div id="dispatchRightContentFooter">
                                                            {!!(cancellationJobs ?? [])
                                                                .length && (
                                                                    <small>
                                                                        {`Displaying ${cancellationJobs.length
                                                                            } of ${cancellationJobsMaxRows} cancellations (${planningJobsMaxRows
                                                                            - cancellationJobsFilteredCount
                                                                            } filtered).`}
                                                                    </small>
                                                                )}
                                                        </div>
                                                    )}
                                                </Hide>
                                            </>
                                        )}
                                    </div>
                                </Split>

                                {Boolean(showAttendance) && (
                                    <Hide mdUp>
                                        <div className="attendance-event-sink">
                                            <DailyAttendanceDispatchSink
                                                dailyAttendanceEvents={
                                                    dailyAttendanceEvents
                                                }
                                                employeeDailyAttendanceEvents={
                                                    employeeDailyAttendanceEvents
                                                }
                                                selectedWeekRangeDays={
                                                    selectedWeekRangeDays
                                                }
                                                loading={loading}
                                                staffLoading={staffLoading}
                                                resourcesLoading={resourcesLoading}
                                                areAttendanceEventsEnabled={
                                                    areAttendanceEventsEnabled
                                                }
                                                staffTabActive={staffTabActive}
                                                selectedAttendanceEvent={this.state.selectedAttendanceEvent}
                                                onAttendanceEventClick={this.handleAttendanceEventClick}
                                            />
                                        </div>
                                    </Hide>
                                )}
                            </div>
                        </div>
                    </DragDropContext>
                </div>

                <CancelJob
                    isOpen={showCancelJobModal}
                    jobCancellationTypes={jobCancellationTypes}
                    jobCancellationReasons={jobCancellationReasons}
                    {...this.state.selectedDayJobToRemove || {}}
                    onClose={() => this.setState({ showCancelJobModal: false, selectedDayJobToRemove: {} })}
                />

                <CancelJobAssignment
                    isOpen={showCancelAssignmentModal}
                    jobAssignmentCancellationTypes={jobAssignmentCancellationTypes}
                    {...this.state.selectedJobAssignmentToRemove || {}}
                    onClose={() => this.setState({ showCancelAssignmentModal: false, selectedJobAssignmentToRemove: {} })}
                />

                {/* Resource Filter Modal */}
                <Modal
                    isOpen={showFilterModal}
                    toggle={this.toggleFilterModal}
                >
                    <ModalHeader toggle={this.toggleFilterModal}>
                        Filter {activeResourceTab.name}
                    </ModalHeader>
                    <ModalBody>
                        {staffTabActive && (
                            <>
                                <ValidatedSelect
                                    id="groups"
                                    isMulti
                                    name="groups"
                                    label="groups"
                                    options={employeeGroups}
                                    getOptionValue={(o) => o.id}
                                    getOptionLabel={(o) => o.groupName}
                                    value={employeeGroups.filter((g) => modalFilterGroups.includes(g.id))}
                                    onChange={(sel) => this.setState({ modalFilterGroups: sel.map((x) => x.id) })}
                                />

                                <ValidatedSelect
                                    id="certifications"
                                    name="certifications"
                                    label="Certifications"
                                    isMulti
                                    options={certificationOptions}
                                    getOptionValue={(o) => o.id}
                                    getOptionLabel={(o) => o.name}
                                    value={certificationOptions.filter((c) => modalFilterCerts.includes(c.id))}
                                    onChange={(sel) => this.setState({ modalFilterCerts: sel.map((x) => x.id) })}
                                />
                            </>
                        )}
                        {equipmentTabActive && (
                            <ValidatedSelect
                                id="equipmentTypes"
                                name="equipmentTypes"
                                label="Equipment Types"
                                isMulti
                                options={equipmentTypes}
                                getOptionValue={(o) => o.id}
                                getOptionLabel={(o) => o.description}
                                value={equipmentTypes.filter((e) => modalFilterEquip.includes(e.id))}
                                onChange={(sel) => this.setState({ modalFilterEquip: sel.map((x) => x.id) })}
                            />
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="secondary"
                            onClick={this.handleFilterModalResetButtonClick}
                        >
                            Reset
                        </Button>
                        <Button
                            color="primary"
                            onClick={this.handleFilterModalSaveButtonClick}
                        >
                            Save
                        </Button>
                    </ModalFooter>
                </Modal>

                <JobWorkflowEditor
                    jobDetails={selectedWorkflowJob}
                    usCounties={this.state.usCounties}
                    isCancelledJob={this.state.isCancelledJobWorkflow}
                    show
                    onCancellationTypeChanged={this.onCancellationTypeChanged}
                    onClose={() => {
                        this.context.setFormOpened(false);
                        this.setState({
                            jobWorkflowModalOpen: false,
                            selectedWorkflowJob: null,
                            isCancelledJobWorkflow: false,
                        });
                    }}
                />

                <Hide smDown>
                    <MobileAssignmentsModal
                        isOpen={showMobileAssignmentModal}
                        onClose={this.closeMobileAssignmentModal}
                        resourceType={mobileAssignmentResourceType}
                        resources={{ staff, equipment }}
                        selectedResource={mobileSelectedResource}
                        onResourceSelect={this.onMobileResourceSelect}
                        onSave={this.onMobileSaveAssignment}
                        slot={mobileAssignmentSlot}
                        job={mobileAssignmentJob}
                        tenantSettings={tenantSettings}
                        hiddenStaffIds={hiddenStaffIds}
                        hiddenEquipmentIds={hiddenEquipmentIds}
                        longTermStatuses={longTermStatuses}
                        employeeLongTermStatuses={employeeLongTermStatuses}
                        jobAssignmentCancellationTypes={jobAssignmentCancellationTypes}
                        isSaving={jobsSaving.includes(mobileAssignmentJob?.id)}
                        allowCrossDispatching={allowCrossDispatching}
                        dispatchEquipmentPermission={dispatchEquipmentPermission}
                        dispatchEmployeePermission={dispatchEmployeePermission}
                        dispatchLocations={dispatchLocations}
                        resourceLocation={(dispatchLocations ?? []).find((x) => x.value === selectedResourceLocation) ?? ''}
                        onSelectedResourceLocationChanged={this.onSelectedResourceLocationChanged}
                        resourceJobs={resourceJobs}
                        daysInRange={daysInRange}
                        dailyAttendanceEvents={dailyAttendanceEvents}
                        employeeDailyAttendanceEvents={employeeDailyAttendanceEvents}
                        cancellationJobs={crossDispatchCancellationJobs}
                        selectedJobDispatchLocation={selectedJobDispatchLocation}
                        resourcesLoading={resourcesLoading}
                        // Filter props
                        selectedWeekRangeDays={selectedWeekRangeDays}
                        areAttendanceEventsEnabled={areAttendanceEventsEnabled}
                        allJobs={allJobs}
                        staffLoading={staffLoading}
                        // Filter state and handlers
                        onlyShowUnassigned={onlyShowUnassigned}
                        onlyShowAttendance={onlyShowAttendance}
                        onlyShowCancelled={onlyShowCancelled}
                        onUnassignedClick={this.onUnassignedClick}
                        onAttendanceClick={this.onAttendanceClick}
                        onCancelledClick={this.onCancelledClick}
                        // Bulk assignment props
                        bulkAssignmentEnabled={mobileIsBulkAssignment}
                        onBulkAssignmentToggle={this.onMobileBulkAssignmentToggle}
                        selectedStaffForEquipment={mobileSelectedStaffForEquipment}
                        onStaffForEquipmentChange={(staff) => this.setState({ mobileSelectedStaffForEquipment: staff })}
                    />
                </Hide>

            </>
        );
    }
}
export default DispatchBoard;
